import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Btn } from "@/components/Btn";
import { ColumnsButton } from "@/components/ColumnsButton";
import {
  allowedColumnsForTab,
  loadColumnWidths,
  loadVisibleColumns,
  resolvePresetColumns,
  saveColumnWidths,
  saveVisibleColumns,
  type ColumnKey,
  type CohortSource,
} from "@/lib/columns-config";
import { I } from "@/components/Icon";
import { UserAlertsBell } from "@/components/UserAlertsBell";
import { MappingGateBanner } from "@/components/MappingGateBanner";
import { Pager } from "@/components/Pager";
import { DateRangePicker } from "@/components/DateRangePicker";
import { SegRange } from "@/components/SegRange";
import { IpmBadge } from "@/components/MetricCells";
import { StatsTable } from "@/components/StatsTable";
import { SearchTermsSplitFooter } from "@/components/SearchTermsSplitFooter";
import { StatusDot } from "@/components/StatusDot";
import { TranslationCell } from "@/components/TranslationCell";
import {
  applyTranslationPatches,
  useTranslationPolling,
} from "@/lib/useTranslationPolling";
import { EntityChartPanel } from "@/components/EntityChartPanel";
import { Tabs, type Tab } from "@/components/Tabs";
import { Topbar } from "@/components/Topbar";
import {
  adsFiltersToStatsFilters,
  useAdsFilters,
  type AdsFilters,
} from "@/lib/ads-filters";
import {
  useAdGroupsMirror,
  useAdGroupsTable,
  useApps,
  useCreateNegativeKeywords,
  useAppCohortConfig,
  useBulkApplyCpp,
  useBulkEditBidBudget,
  useBulkPauseCpp,
  useCampaignCpps,
  useCampaignsTable,
  useColumnPresets,
  useCustomProductPages,
  useDeleteNegativeKeywords,
  useKeywordsTable,
  useLabels,
  useNegativeKeywords,
  useSearchTerms,
  useUpdateAdGroup,
  useUpdateKeyword,
  type BulkEditLevel,
  type CampaignCppRow,
  type CampaignCppsResponse,
  useReadOnlyCampaigns,
} from "@/lib/api-hooks";
import { EditBidBudgetModal } from "@/components/EditBidBudgetModal";
import { AddAsNegativeMenu } from "@/components/AddAsNegativeMenu";
import { ApplyCppModal, type CppOption } from "@/components/ApplyCppModal";
import { BulkActions } from "@/components/BulkActions";
import { RulesManagerModal } from "@/components/RulesManagerModal";
import { SimpleDropdown } from "@/components/SimpleDropdown";
import { GridColumnsMenu } from "@/components/GridColumnsMenu";
import {
  TRANSLATION_COLUMN,
  TRANSLATION_LABEL,
  useGridColumns,
} from "@/lib/grid-column-visibility";
import { CopyKeywordsButton } from "@/components/CopyKeywordsButton";
import { FilterMenuButton } from "@/components/FilterMenuButton";
import { AdsFilterProvider } from "@/components/ads-filter/AdsFilterContext";
import { splitChip, useAdsFilterOptional } from "@/components/ads-filter/context";
import { FilterChips, SplitChip } from "@/components/ads-filter/FilterChips";
import { FilterEmptyState } from "@/components/ads-filter/FilterEmptyState";
import { useChipsPlacement } from "@/components/ads-filter/useChipsPlacement";
import { ADS_TAB_REASON, deviceSubRows } from "@/lib/device-split";
import { rowMatches, zeroRowFacts } from "@/lib/ads-manager-filter";
import { LabelsProvider } from "@/components/LabelsProvider";
import { StatusToast } from "@/components/StatusToast";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { reportBulkAction } from "@/lib/sentry-breadcrumbs";
import { adGroupPassesStatusFilter } from "@/lib/ads-status-filter";
import {
  awaitPendingWrites,
  runWithAsaConfirmToast,
} from "@/lib/pending-writes-tracker";
import { useCanWriteApp } from "@/lib/useAuth";
import { useOrgNavigate, useOrgPath, useOrgPublicId } from "@/lib/useOrgNav";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { fmtInt, fmtMoney, fmtMoneyTrim, fmtPct } from "@/lib/fmt";
import { CampaignStudioProgressBar } from "@/components/CampaignStudioProgressBar";
import type {
  AdGroupStatsRow,
  KeywordStatsRow,
  NegativeKeywordRow,
  SearchTermRow,
} from "@/lib/types";

type TabKey = "ad_groups" | "keywords" | "negative_keywords" | "search_terms" | "ads";

/**
 * Campaign detail page — the "one campaign" landing Ads Manager uses
 * as the drill-in target. Five tabs:
 *
 *   Ad Groups         (config + metrics for ad groups in this campaign)
 *   All Keywords      (flat list; filtered by selected ad groups)
 *   All Negative Kwds (campaign- or ad-group-scoped negatives)
 *   All Search Terms  (user queries that matched)
 *   All Ads           (ASA creatives)
 *
 * The checkbox state in the Ad Groups tab doubles as a filter when the
 * user switches to the Keywords tab — "select some ad groups, then
 * switch to Keywords to see only those".
 */
function adGroupFromParams(params: URLSearchParams): number | null {
  const n = Number(params.get("ad_group"));
  return Number.isInteger(n) && n > 0 ? n : null;
}

export function CampaignDetailPage() {
  const { campaignId: raw } = useParams<{ campaignId: string }>();
  const campaignId = Number(raw);
  const orgPath = useOrgPath();

  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useAdsFilters();

  const activeTab = (params.get("tab") as TabKey) || "ad_groups";
  const setActiveTab = (t: TabKey) => {
    // ТЗ §2.3 — переход на другой уровень закрывает график: иначе на
    // экране осталась бы линия по сущности, которой на новой вкладке
    // нет, с чужим именем в шапке.
    setChartOpen(false);
    const next = new URLSearchParams(params);
    if (t === "ad_groups") next.delete("tab");
    else next.set("tab", t);
    setParams(next, { replace: false });
  };

  const statsFilters = useMemo(() => adsFiltersToStatsFilters(filters), [filters]);

  // Fetch the campaign row to source its name + status for the header.
  // Cached from the previous /ads list view typically.
  const campaignQuery = useCampaignsTable(
    { ...statsFilters, campaign_ids: [campaignId] },
    { limit: 1 }
  );
  const campaign = campaignQuery.data?.rows.find((c) => c.campaign_id === campaignId);
  const campaignName = campaign?.campaign_name ?? `Campaign ${campaignId}`;
  // Per-app edit gate (§51) — editable only when the user can write THIS
  // campaign's app. Passed down to every tab so they don't re-gate by role.
  // Кампания группы Apple с доступом только на чтение — правки выключены
  // (ТЗ apple_signin_connect §10).
  const campaignReadOnly = !!useReadOnlyCampaigns().data?.has(campaignId);
  const canEdit = useCanWriteApp(campaign?.app_id ?? null) && !campaignReadOnly;

  // Resolve the ASA adamId for the campaign's app — the CPP picker
  // needs it to filter the creatives list.
  const appsQuery = useApps();
  const campaignAsaAppId =
    (appsQuery.data ?? []).find((a) => a.id === (campaign?.app_id ?? null))
      ?.asa_app_id ?? null;
  // Cohort source of this campaign's app — drives which conversion-event
  // columns the Ad Groups / Keywords tabs offer ('adjust' → 10 Adjust
  // events, else Adapty).
  const cohortCfg = useAppCohortConfig(campaign?.app_id ?? null);
  const cohortSource = cohortCfg.data?.source;
  // Gate the per-tab default-preset auto-apply until cohortSource is
  // DEFINITIVELY known. Two waits, both required:
  //  1. `!campaignQuery.isLoading` — until the campaign row loads we
  //     don't know its app_id, so the cohort query is disabled and
  //     reports isLoading=false (a disabled TanStack v5 query is not
  //     "in flight"). Without this wait, cohortReady fires true while
  //     the campaign is still loading → tabs apply the preset with
  //     cohortSource=undefined → an Adjust app's adj_* columns get
  //     stripped and appliedDefaultRef burns, so the correct preset
  //     never re-applies. This was the "columns reset every time I
  //     open a campaign" bug (the 2026-05-30 isLoading fix alone was
  //     not enough — it still fired before the campaign loaded).
  //  2. `!cohortCfg.isLoading` — once app_id is known the cohort query
  //     enables; wait for it to resolve so cohortSource is the real
  //     value, not undefined.
  // (When the campaign genuinely has no app the cohort query stays
  // disabled → cohortReady becomes true with cohortSource=undefined,
  // which is correct — there's no Adjust source to wait for.)
  const cohortReady = !campaignQuery.isLoading && !cohortCfg.isLoading;

  // Bulk-action selection on the Ad Groups tab — checked rows for
  // "Apply CPP", "Edit bids", etc. NOTE: this used to be reused as
  // the Keywords-tab narrowing too (until 2026-05-10). User feedback:
  // "All Keywords" should show every keyword in the campaign
  // regardless of checked ad-groups; only an explicit drill-in
  // (clicking a single ad-group row) narrows the keyword list. So
  // selection-for-bulk and drill-narrowing are now separate states.
  const [selectedAdGroups, setSelectedAdGroups] = useState<Set<number>>(new Set());

  // Drill-narrowing state: the one ad-group the user "fell into" by
  // clicking its row on the Ad Groups tab. NULL = "All Keywords"
  // mode (full campaign). Set to a single ad-group id when the user
  // drills in. Cleared when the user navigates back to Ad Groups.
  // `?ad_group=<id>&ad_group_name=<name>` — сюда ведёт ссылка на группу из
  // колокольчика: открыть ключи этой группы. Параметры читаются один раз и
  // снимаются с адреса (эффект ниже), иначе выход из группы вернул бы её.
  const [drilledAdGroup, setDrilledAdGroup] = useState<number | null>(() =>
    adGroupFromParams(params),
  );
  // ★ Один флаг на обе вкладки, но график принадлежит ТЕКУЩЕЙ: уровень
  // берётся из активной вкладки, и переход между ними по ТЗ график
  // закрывает — иначе на экране осталась бы линия по сущности, которой
  // на новой вкладке нет.
  const [chartOpen, setChartOpen] = useState(false);
  const [agSingle, setAgSingle] = useState<{ id: number; name: string } | null>(
    null,
  );
  // Name of the drilled ad-group, captured at drill-in time so the
  // breadcrumb can show it (the row carries the name; the id alone isn't
  // enough). Cleared together with drilledAdGroup.
  const [drilledAdGroupName, setDrilledAdGroupName] = useState<string | null>(
    () => (adGroupFromParams(params) !== null ? params.get("ad_group_name") : null),
  );
  useEffect(() => {
    if (!params.has("ad_group")) return;
    const ag = adGroupFromParams(params);
    if (ag !== null) {
      setDrilledAdGroup(ag);
      setDrilledAdGroupName(params.get("ad_group_name"));
    }
    const next = new URLSearchParams(params);
    next.delete("ad_group");
    next.delete("ad_group_name");
    setParams(next, { replace: true });
  }, [params, setParams]);

  // In-campaign "Edit bids" — opens the shared EditBidBudgetModal pinned
  // to one level (ad_group / keyword), scoped to the selected entities.
  const [editBids, setEditBids] = useState<{
    level: BulkEditLevel;
    ids: number[];
  } | null>(null);
  const [editBidsToast, setEditBidsToast] = useState<{
    msg: string;
    variant: "success" | "error";
  } | null>(null);
  const bulkEditBidBudget = useBulkEditBidBudget();
  useEffect(() => {
    if (!editBidsToast) return;
    const t = setTimeout(() => setEditBidsToast(null), 4500);
    return () => clearTimeout(t);
  }, [editBidsToast]);

  // After a child status change (ad_group / keyword enable-pause), the
  // backend re-checks the campaign's ASA serving status over ~90s — Apple
  // recomputes displayStatus with a lag (e.g. ON_HOLD → RUNNING once the
  // first ad_group is enabled). Refetch the header on the same cadence so
  // it flips without a manual reload.
  const servingPulseTimers = useRef<number[]>([]);
  const pulseCampaignServing = () => {
    servingPulseTimers.current.forEach((t) => clearTimeout(t));
    servingPulseTimers.current = [8000, 25000, 55000, 95000].map((ms) =>
      window.setTimeout(() => void campaignQuery.refetch(), ms),
    );
  };
  useEffect(
    () => () => servingPulseTimers.current.forEach((t) => clearTimeout(t)),
    [],
  );

  // Bridged up from KeywordsTab so the page-level filter strip can
  // render a Copy button next to the date picker when ≥1 keyword is
  // selected. KeywordsTab owns the actual selection state.
  const [kwExposed, setKwExposed] = useState<KeywordsTabExposed>({
    selectedCount: 0,
    getSelectedTexts: () => [],
    single: null,
  });

  const query = toQueryString(filters);

  const tabs: Tab<TabKey>[] = [
    { key: "ad_groups", label: "Ad Groups" },
    { key: "keywords", label: "All Keywords" },
    { key: "negative_keywords", label: "All Negative Keywords" },
    { key: "search_terms", label: "All Search Terms" },
    { key: "ads", label: "All Ads" },
  ];

  // Right-aligned filter strip + bell, mirroring Overview's header
  // and CampaignsPage so the pickers live in the same place across
  // every Ads Manager level. `.ov-filter-row` enforces a 52px row so
  // Filter / Copy / Date / Bell line up on identical baselines.
  const rightSlot = (
    <div
      className="row ov-filter-row"
      style={{
        marginLeft: "auto",
        alignItems: "stretch",
        height: 52,
        gap: 10,
        flex: "0 0 auto",
        flexWrap: "nowrap",
        justifyContent: "flex-end",
      }}
    >
      {(activeTab === "ad_groups" || activeTab === "keywords") && (
        <FilterMenuButton />
      )}
      {activeTab === "search_terms" && <FilterMenuButton splitOnly />}
      {activeTab === "ads" && (
        <FilterMenuButton splitOnly splitDisabledReason={ADS_TAB_REASON} />
      )}
      {activeTab === "keywords" && kwExposed.selectedCount > 0 && (
        <CopyKeywordsButton
          count={kwExposed.selectedCount}
          getKeywordTexts={kwExposed.getSelectedTexts}
        />
      )}
      <DateRangePicker
        preset={filters.preset}
        range={filters.range}
        mode={filters.mode}
        onChange={({ preset, range, mode }) =>
          setFilters({ preset, range, mode })
        }
      />
      <UserAlertsBell />
    </div>
  );

  return (
    <LabelsProviderWrapper
      grain={activeTab === "keywords" ? "keywords" : "ad_groups"}
      appId={campaign?.app_id ?? "all"}
    >
      <Topbar title={campaignName} rightSlot={rightSlot} />
      {/* Ход Apply CPP. Тот же бар, что у Campaign Studio, и та же
          позиция — между Topbar и содержимым. Ничего не рисует, пока
          нет активного задания. Применение CPP синхронно и занимает по
          секунде на группу; без бара две минуты выглядят как «не
          сработало» (замер 24.08: 117 с, HTTP 200, и человек всё равно
          решил, что применение не прошло). */}
      <CampaignStudioProgressBar />

      <div className="content ads-density">
        {/* Sticky-stacked breadcrumb row — never scrolls out of view.
            Matches the CampaignsPage trio (topbar / crumbs / table). */}
        <div className="ads-sticky-crumbs">
          <Breadcrumbs
            items={[
              { label: "Account", to: orgPath(`/ads${query}`) },
              { label: "Campaigns", to: orgPath(`/ads${query}`) },
              // When drilled into a single ad-group, the campaign crumb
              // becomes a click-to-exit-drill link and the ad-group name
              // is the current (last) crumb.
              ...(drilledAdGroup !== null
                ? [
                    {
                      label: campaignName,
                      onClick: () => {
                        setDrilledAdGroup(null);
                        setDrilledAdGroupName(null);
                        setActiveTab("ad_groups");
                      },
                    },
                    {
                      label:
                        drilledAdGroupName || `Ad group #${drilledAdGroup}`,
                    },
                  ]
                : [{ label: campaignName }]),
            ]}
          />
        </div>
        {campaignReadOnly && (
          <div
            className="muted"
            style={{ fontSize: 12.5, margin: "4px 0 10px" }}
            title="Read-only access — reconnect with Read & Write to enable changes."
          >
            Read-only access — reconnect with Read &amp; Write to enable changes.
          </div>
        )}

        {/* Big page header with campaign name + status dot.
            ★ Прячется при открытом графике — решение владельца: на
            уровнях ad group и ключей имя кампании написано крупно и
            съедает высоту, которая нужна графику. Крошки при этом
            остаются ВСЕГДА и стоят над графиком. */}
        {!chartOpen && (
        <div
          className="row gap-12"
          style={{ marginTop: 4, marginBottom: 16, alignItems: "center" }}
        >
          <h1
            className="page-title"
            style={{ fontSize: 34, margin: 0, fontWeight: 500, letterSpacing: "-0.01em" }}
          >
            {campaignName}
          </h1>
          <StatusDot
            status={campaign?.status ?? null}
            // Prefer ASA's deliverability state (RUNNING / ON_HOLD /
            // …) over the user-set ENABLED/PAUSED toggle — the
            // header dot should match what Apple's own ASA console
            // shows, not a misleading green when the campaign is
            // actually on hold.
            displayStatus={campaign?.display_status ?? null}
            withLabel={false}
          />
        </div>
        )}

        {chartOpen && (activeTab === "ad_groups" || activeTab === "keywords") && (
          <div className="ads-chart-slot">
            <EntityChartPanel
              level={activeTab === "ad_groups" ? "ad_group" : "keyword"}
              appId={campaign?.app_id ?? null}
              campaignId={campaignId}
              entityId={
                activeTab === "ad_groups"
                  ? (agSingle?.id ?? null)
                  : (kwExposed.single?.id ?? null)
              }
              entityName={
                activeTab === "ad_groups"
                  ? (agSingle?.name ?? null)
                  : (kwExposed.single?.name ?? null)
              }
              dateFrom={ymd(filters.range.from)}
              dateTo={ymd(filters.range.to)}
              onClose={() => setChartOpen(false)}
            />
          </div>
        )}

        {/* Tab strip — sticky-stacked with the breadcrumbs so user
            can still switch tabs while scrolling rows. */}
        <div className="ads-sticky-toolbar" style={{ paddingBottom: 0 }}>
          <Tabs<TabKey> tabs={tabs} value={activeTab} onChange={setActiveTab} />
        </div>

        {/* Active-tab content. `cohortReady` tells each tab whether
            the parent's cohortCfg query has finished — false during
            the initial fetch, true once data resolved (whether to a
            value or null). Tabs use it to defer the default-preset
            auto-apply until cohortSource is known; otherwise the
            apply runs with undefined and adj_* / Adapty event cols
            get wrongly filtered for the user's preset. */}
        {activeTab === "ad_groups" && (
          <AdGroupsTab
            campaignId={campaignId}
            campaignCountries={campaign?.countries ?? []}
            asaAppId={campaignAsaAppId}
            canEdit={canEdit}
            appId={campaign?.app_id ?? null}
            cohortSource={cohortSource}
            cohortReady={cohortReady}
            statsFilters={statsFilters}
            selection={selectedAdGroups}
            onSelectionChange={setSelectedAdGroups}
            onSingleChange={setAgSingle}
            onToggleChart={() => setChartOpen((v) => !v)}
            chartOpen={chartOpen}
            onDrillIn={(adGroupId, adGroupName) => {
              // Drill-in narrows Keywords to ONE ad-group. Doesn't
              // touch the bulk-selection set.
              setDrilledAdGroup(adGroupId);
              setDrilledAdGroupName(adGroupName);
              setActiveTab("keywords");
            }}
            onEditBids={(ids) => setEditBids({ level: "ad_group", ids })}
            onStatusChanged={pulseCampaignServing}
          />
        )}
        {activeTab === "keywords" && (
          <KeywordsTab
            campaignId={campaignId}
            cohortSource={cohortSource}
            cohortReady={cohortReady}
            statsFilters={statsFilters}
            onEditBids={(ids) => setEditBids({ level: "keyword", ids })}
            onStatusChanged={pulseCampaignServing}
            // null = "All Keywords" view (whole campaign). Non-null
            // = drilled into a specific ad-group.
            filterAdGroupIds={
              drilledAdGroup !== null ? [drilledAdGroup] : []
            }
            drilledAdGroup={drilledAdGroup}
            drilledAdGroupName={drilledAdGroupName}
            onExitDrill={() => {
              setDrilledAdGroup(null);
              setDrilledAdGroupName(null);
            }}
            canEdit={canEdit}
            appId={campaign?.app_id ?? null}
            onExposedChange={setKwExposed}
            onToggleChart={() => setChartOpen((v) => !v)}
            chartOpen={chartOpen}
          />
        )}
        {activeTab === "negative_keywords" && (
          <NegativeKeywordsTab campaignId={campaignId} canEdit={canEdit} />
        )}
        {activeTab === "search_terms" && (
          <SearchTermsTab
            campaignId={campaignId}
            canEdit={canEdit}
            appId={campaign?.app_id ?? null}
            statsFilters={statsFilters}
            // Narrow by drill-down only, NOT by bulk-selection — same
            // model as KeywordsTab (fixed 2026-05-10).
            filterAdGroupIds={
              drilledAdGroup !== null ? [drilledAdGroup] : []
            }
          />
        )}
        {activeTab === "ads" && (
          <AdsTab
            campaignId={campaignId}
            statsFilters={statsFilters}
            filterAdGroupIds={
              drilledAdGroup !== null ? [drilledAdGroup] : []
            }
          />
        )}
      </div>

      {/* Shared bulk-edit modal — same one as the campaign-level Edit
          Bid/Budget, but pinned to one level (no Campaign tab) and scoped
          to the selected ad_groups / keywords. */}
      {editBids && campaign && (
        <EditBidBudgetModal
          open
          onClose={() => setEditBids(null)}
          selectedCampaigns={[
            {
              campaign_id: campaignId,
              bidding_strategy: campaign.bidding_strategy ?? null,
            },
          ]}
          filters={filters}
          restrictLevel={editBids.level}
          scopeAdGroupIds={
            editBids.level === "ad_group" ? editBids.ids : undefined
          }
          scopeKeywordIds={
            editBids.level === "keyword" ? editBids.ids : undefined
          }
          scopeCount={editBids.ids.length}
          onApply={(req) => {
            // Fire-and-forget — same lifecycle as the campaign-level
            // modal. Mutation runs in the background; the toast appears
            // regardless of whether the modal is still open.
            bulkEditBidBudget.mutate(req, {
              onSuccess: (res) => {
                let variant: "success" | "error" = "success";
                let msg: string;
                if (res.success_count > 0 && res.failure_count === 0) {
                  msg = `${res.success_count} ${res.field_label} changed`;
                } else if (res.success_count > 0 && res.failure_count > 0) {
                  variant = "error";
                  msg = `${res.success_count} ${res.field_label} changed, ${res.failure_count} failed. Try again`;
                } else if (res.failure_count > 0) {
                  variant = "error";
                  msg = `${res.failure_count} ${res.field_label} didn't change. Try again`;
                } else {
                  msg = "No matching entities to change";
                }
                setEditBidsToast({ msg, variant });
              },
              onError: (e) => {
                setEditBidsToast({
                  msg: `Bulk edit failed: ${e instanceof Error ? e.message : String(e)}`,
                  variant: "error",
                });
              },
            });
            setEditBids(null);
          }}
        />
      )}
      {editBidsToast && (
        <StatusToast
          message={editBidsToast.msg}
          variant={editBidsToast.variant}
        />
      )}
    </LabelsProviderWrapper>
  );
}

/** Tiny wrapper that fetches the labels list once for the page subtree —
 *  and hosts the Ads Manager filter for the active tab's grain (its state
 *  is per account × app; the tab's grain picks the label set and the
 *  metric list). */
function LabelsProviderWrapper({
  grain,
  appId,
  children,
}: {
  grain: "ad_groups" | "keywords";
  appId: number | "all";
  children: React.ReactNode;
}) {
  const labelsQuery = useLabels();
  const accountId = useOrgPublicId() ?? "0";
  return (
    <LabelsProvider labels={labelsQuery.data ?? []}>
      <AdsFilterProvider
        key={grain}
        grain={grain}
        accountId={accountId}
        appId={appId}
        labels={labelsQuery.data ?? []}
      >
        {children}
      </AdsFilterProvider>
    </LabelsProvider>
  );
}

// ===========================================================================
// Ad Groups tab
// ===========================================================================

function AdGroupsTab({
  campaignId,
  campaignCountries,
  asaAppId,
  canEdit,
  appId,
  cohortSource,
  cohortReady,
  statsFilters,
  selection,
  onSelectionChange,
  onDrillIn,
  onEditBids,
  onStatusChanged,
  onSingleChange,
  onToggleChart,
  chartOpen,
}: {
  campaignId: number;
  /** Parent campaign's countriesOrRegions — the Country filter reads them
   *  for ad groups (TZ §3.4); only mirror-added rows are checked here,
   *  the server already filtered the stats rows. */
  campaignCountries: string[];
  /** ASA adamId of the campaign's app — drives the CPP picker. */
  asaAppId: number | null;
  /** Per-app edit gate from the parent (§51) — replaces the old
   *  account-role check so a per-app writer can manage this app. */
  canEdit: boolean;
  /** Приложение кампании — им фильтруется список правил. */
  appId: number | null;
  cohortSource?: CohortSource;
  cohortReady: boolean;
  statsFilters: ReturnType<typeof adsFiltersToStatsFilters>;
  selection: Set<number>;
  onSelectionChange: (next: Set<number>) => void;
  /** Единственная выделенная группа — для встроенного графика. Имя есть
   *  только здесь: страница держит идентификаторы, а строки грузит
   *  вкладка. `null` при нуле и при двух — график строится по одной. */
  onSingleChange?: (one: { id: number; name: string } | null) => void;
  /** Кнопка «Performance over time» в панели действий вкладки. Флаг
   *  живёт в СТРАНИЦЕ: панель исчезает при снятии выделения, а график
   *  обязан остаться и показать placeholder. */
  onToggleChart: () => void;
  chartOpen: boolean;
  onDrillIn: (adGroupId: number, adGroupName: string) => void;
  /** Open the shared bulk-edit modal pinned to the ad_group level,
   *  scoped to the passed selected ad_group ids. */
  onEditBids: (ids: number[]) => void;
  /** Notify the parent that a child status flip happened, so it can
   *  pulse-refetch the campaign header (ASA serving status lags). */
  onStatusChanged?: () => void;
}) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "paused">("all");
  const [visibleCols, _setVisibleColsState] = useState<ColumnKey[]>(() =>
    loadVisibleColumns("ad_groups")
  );
  // See CampaignsPage's setVisibleCols wrapper comment — persists
  // preset/modal/drag-reorder changes to localStorage so they survive
  // a page reload without re-applying the preset manually.
  const setVisibleCols = (
    next: ColumnKey[] | ((prev: ColumnKey[]) => ColumnKey[]),
  ) => {
    _setVisibleColsState((prev) => {
      const resolved = typeof next === "function" ? next(prev) : next;
      saveVisibleColumns("ad_groups", resolved);
      return resolved;
    });
  };
  const [colWidths, setColWidths] = useState<Record<string, number>>(() =>
    loadColumnWidths("ad_groups"),
  );
  const onWidthChange = (key: string, px: number) => {
    setColWidths((prev) => {
      const next = { ...prev, [key]: px };
      saveColumnWidths("ad_groups", next);
      return next;
    });
  };
  // Columns actually rendered, filtered by the app's cohort source —
  // Adjust apps never render Adapty-sourced columns even if a stale
  // localStorage set lists them.
  const shownCols = useMemo(() => {
    const allowed = new Set(allowedColumnsForTab("ad_groups", cohortSource));
    return visibleCols.filter((k) => allowed.has(k));
  }, [visibleCols, cohortSource]);
  // Auto-apply the user's default ad_groups preset once /api/column-
  // presets resolves. See the matching effect in CampaignsPage for the
  // rationale + ordering. `appliedDefaultRef` so subsequent
  // invalidations don't re-stomp manual changes.
  //
  // Gated on `cohortReady` — without this, the apply runs while
  // cohortSource is still `undefined` (parent's cohortCfg query
  // pending), allowedColumnsForTab falls through to the default
  // "drop adj_*" branch, and the user's preset has its adj_* cols
  // stripped wrongly for Adjust apps. The ref protects against later
  // re-fires, so we MUST wait until cohortSource is resolved before
  // the first apply.
  const presetsQuery = useColumnPresets("ad_groups");
  const appliedDefaultRef = useRef(false);
  useEffect(() => {
    if (appliedDefaultRef.current) return;
    if (!presetsQuery.data) return;
    if (!cohortReady) return;
    appliedDefaultRef.current = true;
    const dft = presetsQuery.data.find((p) => p.is_default);
    if (!dft) return;
    setVisibleCols(
      resolvePresetColumns(
        "ad_groups",
        dft.columns,
        allowedColumnsForTab("ad_groups", cohortSource),
      ),
    );
    const w = dft.widths ?? {};
    setColWidths(w);
    saveColumnWidths("ad_groups", w);
  }, [presetsQuery.data, cohortReady]); // eslint-disable-line react-hooks/exhaustive-deps
  const [sort, setSort] = useState<{ by: string | null; dir: "asc" | "desc" }>(
    { by: null, dir: "desc" }
  );

  const adsFilter = useAdsFilterOptional();
  const agToolbarRef = useRef<HTMLDivElement>(null);
  const agChipsRef = useRef<HTMLDivElement>(null);
  const agChipsWrapped = useChipsPlacement(agToolbarRef, agChipsRef, !!adsFilter?.showChips);
  // Search here filters the loaded rows in the browser. With «Split by
  // devices» on it also goes to the server, so the footer's Total for
  // iPhone / iPad (summed server-side over every filtered ad group) covers
  // the same searched set as the rows; without the split nothing changes.
  const agSearchForServer = useDebouncedValue(search, 350).trim();
  const statsQuery = useAdGroupsTable(
    statsFilters, campaignId, { limit: 500 }, sort,
    adsFilter?.splitOn ? agSearchForServer || undefined : undefined, statusFilter,
    adsFilter?.apiParam, adsFilter?.splitOn);
  const mirrorQuery = useAdGroupsMirror(campaignId);
  const gate = statsQuery.data?.gate;
  const updateAdGroup = useUpdateAdGroup();

  // Apply-CPP modal — wired to live ASA endpoints. Apply against this
  // single campaign so the user doesn't have to leave the detail page
  // to push a new CPP.
  const [applyCppOpen, setApplyCppOpen] = useState(false);
  const [rulesManagerOpen, setRulesManagerOpen] = useState(false);
  const navigate = useOrgNavigate();
  const [toast, setToast] = useState<
    {
      msg: string;
      variant: "success" | "error";
      action?: { label: string; onClick: () => void };
    } | null
  >(null);
  // Confirmation gate for bulk Pause/Enable. Same rationale as in
  // CampaignsPage — the silent-mass-mutation class of mistake (32
  // CTMOBI campaigns paused without notification on 2026-05-04)
  // could just as easily land here.
  const [agStatusConfirm, setAgStatusConfirm] = useState<
    { target: "ENABLED" | "PAUSED"; ids: number[] } | null
  >(null);
  const cppQuery = useCustomProductPages(asaAppId);
  // See CampaignsPage for why product_page_id is the unique key —
  // creative_id collides at 0 across Default + unwrapped CPPs.
  const cppOptions: CppOption[] = (cppQuery.data?.rows ?? []).map((c) => ({
    id: c.product_page_id ?? "default",
    name: c.name,
    screenshots: c.screenshots ?? [],
    externalUrl: c.external_url ?? undefined,
  }));
  const bulkApplyCpp = useBulkApplyCpp();
  const flashToast = (
    msg: string,
    variant: "success" | "error" = "success",
    action?: { label: string; onClick: () => void },
  ) => {
    setToast({ msg, variant, action });
    window.setTimeout(
      () => setToast(null),
      action ? 8000 : variant === "error" ? 5000 : 2400,
    );
  };
  const selectedIds = useMemo(() => Array.from(selection), [selection]);

  const fanoutAdGroupStatus = async (
    target: "ENABLED" | "PAUSED",
    ids: number[],
  ): Promise<{
    accepted: { ad_group_id: number; pending_write_id: number }[];
    rejected: { ad_group_id: number; error: string }[];
  }> => {
    const settled = await Promise.allSettled(
      ids.map(async (ad_group_id) => {
        const handle = await updateAdGroup.mutateAsync({
          ad_group_id,
          status: target,
        });
        return { ad_group_id, pending_write_id: handle.pending_write_id };
      }),
    );
    const accepted: { ad_group_id: number; pending_write_id: number }[] = [];
    const rejected: { ad_group_id: number; error: string }[] = [];
    settled.forEach((r, i) => {
      if (r.status === "fulfilled") accepted.push(r.value);
      else rejected.push({
        ad_group_id: ids[i]!,
        error: r.reason instanceof Error ? r.reason.message : String(r.reason),
      });
    });
    return { accepted, rejected };
  };

  const runAgBulkStatus = async (
    target: "ENABLED" | "PAUSED",
    ids: number[],
  ) => {
    if (ids.length === 0) return;
    // Clear the selection on dispatch so the NEXT selection starts
    // fresh instead of accumulating on top of the just-actioned set
    // (`ids` is captured, so the in-flight fanout still completes).
    onSelectionChange(new Set());
    // Enabling/pausing ad_groups can flip the parent campaign's serving
    // status — kick the parent's header pulse so it converges quickly.
    onStatusChanged?.();
    reportBulkAction({
      resource: "ad_group",
      action: target === "PAUSED" ? "pause" : "enable",
      count: ids.length,
      details: { ids: ids.slice(0, 100) },
    });

    const verbPast = target === "PAUSED" ? "paused" : "enabled";
    const reverse: "ENABLED" | "PAUSED" =
      target === "PAUSED" ? "ENABLED" : "PAUSED";

    // Phase 1: queue with our API. No toast yet.
    const { accepted, rejected } = await fanoutAdGroupStatus(target, ids);
    if (rejected.length === ids.length) {
      flashToast(
        `Failed to ${target === "PAUSED" ? "pause" : "enable"} ${ids.length} ad group${ids.length === 1 ? "" : "s"}: ${rejected[0]!.error}`,
        "error",
      );
      return;
    }

    // Phase 2: wait for ASA confirmation per id.
    const tracked = accepted
      .filter((a) => a.pending_write_id > 0)
      .map((a) => ({ id: a.pending_write_id, resourceType: "ad_group" as const }));
    const results = await awaitPendingWrites(tracked);

    let confirmed = 0;
    let failed = rejected.length;
    let timedOut = 0;
    let lastError: string | null = null;
    for (const r of results.values()) {
      if (r.status === "synced") confirmed++;
      else if (r.status === "error") {
        failed++;
        lastError = r.errorMessage ?? lastError;
      }
      else timedOut++;
    }

    if (confirmed === ids.length) {
      flashToast(
        `${confirmed} ad group${confirmed === 1 ? "" : "s"} ${verbPast} on ASA`,
        "success",
        {
          label: "Undo",
          onClick: () => {
            void runAgBulkStatus(reverse, ids);
            setToast(null);
          },
        },
      );
    } else if (confirmed > 0) {
      const detail =
        failed > 0
          ? `${failed} failed${lastError ? ` (${lastError.slice(0, 80)})` : ""}`
          : `${timedOut} still syncing`;
      flashToast(`${confirmed} ${verbPast}, ${detail}`, "error");
    } else if (timedOut === ids.length) {
      flashToast(`ASA hasn't confirmed yet — try again in a moment.`, "error");
    } else {
      flashToast(
        `Failed to ${target === "PAUSED" ? "pause" : "enable"} ${ids.length} ad group${ids.length === 1 ? "" : "s"}${lastError ? `: ${lastError.slice(0, 80)}` : ""}`,
        "error",
      );
    }
  };

  const bulkSetStatus = (target: "ENABLED" | "PAUSED") => {
    if (selectedIds.length === 0) return;
    if (selectedIds.length >= 2) {
      setAgStatusConfirm({ target, ids: selectedIds });
      return;
    }
    void runAgBulkStatus(target, selectedIds);
  };

  // Merge stats rows with mirror data so cpa_goal / default_bid
  // render alongside metrics.
  const rows = useMemo<AdGroupStatsRow[]>(() => {
    const stats = statsQuery.data?.rows ?? [];
    const mirrorById = new Map(
      (mirrorQuery.data ?? []).map((m) => [m.ad_group_id, m])
    );
    const merged: AdGroupStatsRow[] = [];

    // Start with all stats rows, enrich with mirror.
    // Mirror is OPTIONAL — if the mirror endpoint failed (e.g. its
    // pre-2026-04-29 ValidationError) we'd otherwise overwrite stats
    // values with null, blanking out cells the user expects. Order:
    // mirror wins when present, stats falls through, null only as
    // last resort. Same fix shape applied to keywords merge below.
    const seen = new Set<number>();
    for (const s of stats) {
      const m = mirrorById.get(s.ad_group_id);
      const sFields = s as Record<string, unknown>;
      merged.push({
        ...s,
        cpa_goal:
          m?.cpa_goal ?? (sFields.cpa_goal as string | null | undefined) ?? null,
        default_bid:
          m?.default_bid ?? (sFields.default_bid as string | null | undefined) ?? null,
        // has_cpp same pattern — stats authoritative if mirror absent.
        has_cpp: m?.has_cpp ?? s.has_cpp,
      });
      seen.add(s.ad_group_id);
    }
    // Add mirror-only rows (no stats yet — freshly created, or paused
    // long enough to have no traffic in the range). The server already
    // applied the status filter to the stats rows; these bypassed it and
    // put paused ad groups under "Active only" — so they take the same
    // test here.
    for (const m of mirrorQuery.data ?? []) {
      if (seen.has(m.ad_group_id)) continue;
      if (!adGroupPassesStatusFilter(m.status, statusFilter)) continue;
      // The server filtered the stats rows; a mirror-only row (no traffic
      // in the period) takes the same filter here with zero metrics.
      if (
        adsFilter?.isActive &&
        // Rules: a mirror-only row has no server verdict → `ruleIds`
        // undefined → a Rules condition hides it (see RowFacts).
        !rowMatches(adsFilter.applied, zeroRowFacts(adsFilter.labelIdsOf(m.ad_group_id), campaignCountries, undefined))
      ) continue;
      merged.push({
        campaign_id: m.campaign_id,
        ad_group_id: m.ad_group_id,
        ad_group_name: m.name,
        has_cpp: m.has_cpp,
        status: m.status,
        spend_usd: "0",
        impressions: 0,
        taps: 0,
        installs: 0,
        trials: null,
        subscriptions: null,
        revenue_usd: null,
        roas: null,
        cpa: null,
        trial_cvr: null,
        paid_cvr: null,
        roas_d4: null,
        roas_d7: null,
        roas_d30: null,
        cpa_goal: m.cpa_goal,
        default_bid: m.default_bid,
      });
    }
    return merged;
  }, [statsQuery.data, mirrorQuery.data, statusFilter, adsFilter, campaignCountries]);

  // Наверх — единственная выделенная группа, для встроенного графика.
  // Имя живёт только здесь: страница держит идентификаторы, а строки
  // грузит вкладка. Тот же приём, что у ключей (`onExposedChange`).
  useEffect(() => {
    const one =
      selection.size === 1
        ? (rows.find((r) => selection.has(r.ad_group_id)) ?? null)
        : null;
    onSingleChange?.(
      one
        ? { id: one.ad_group_id, name: one.ad_group_name ?? `#${one.ad_group_id}` }
        : null,
    );
  }, [selection, rows, onSingleChange]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q) {
        const nameMatch = (r.ad_group_name ?? "").toLowerCase().includes(q);
        const idMatch = String(r.ad_group_id ?? "").includes(q);
        if (!nameMatch && !idMatch) return false;
      }
      return true;
    });
  }, [rows, search]);

  // A freshly-created campaign has zero traffic → statsQuery resolves
  // empty almost instantly while the meta-mirror (which actually holds
  // the ad groups) is still loading. Gate the empty-state on the mirror
  // too when stats is empty, so we don't flash/persist "no ad groups"
  // before the mirror lands. Established campaigns (stats has rows)
  // render immediately — the mirror only enriches them.
  const listLoading =
    statsQuery.isLoading ||
    (mirrorQuery.isLoading && (statsQuery.data?.rows?.length ?? 0) === 0);

  const toggle = (id: number) => {
    const next = new Set(selection);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };
  const toggleAll = (ids: number[], selectAll: boolean) => {
    const next = new Set(selection);
    for (const id of ids) {
      if (selectAll) next.add(id);
      else next.delete(id);
    }
    onSelectionChange(next);
  };

  return (
    <div className="table-wrap" style={{ borderTop: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
      <div ref={agToolbarRef} className="table-toolbar" style={{ flexWrap: "nowrap", gap: 10 }}>
        <div className="input-group" style={{ width: 280, flex: "none" }}>
          {I.search}
          <input
            className="input"
            placeholder="Search ad groups"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <SegRange<"all" | "active" | "paused">
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: "all", label: "All" },
            { value: "active", label: "Active" },
            { value: "paused", label: "Paused" },
          ]}
        />
        <BulkActions
          selectedIds={selectedIds}
          canEdit={canEdit}
          onEnable={() => void bulkSetStatus("ENABLED")}
          onPause={() => void bulkSetStatus("PAUSED")}
          busy={updateAdGroup.isPending}
          entityType="ad_group"
          onToggleChart={onToggleChart}
          chartOpen={chartOpen}
          showActions
          showRules
          onRulesManager={() => setRulesManagerOpen(true)}
          onActionPick={(v) => {
            if (v === "apply_cpp") setApplyCppOpen(true);
            else if (v === "edit_bids") {
              if (selectedIds.length) onEditBids(selectedIds);
            } else window.alert(`${v} — wired in v1.1`);
          }}
        />
        {adsFilter?.showChips && !agChipsWrapped && (
          <div ref={agChipsRef} style={{ flex: "none" }}>
            <FilterChips applied={adsFilter.applied} describe={adsFilter.describe} onOpen={adsFilter.openModal} onRemove={adsFilter.removeType} split={splitChip(adsFilter)} />
          </div>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flex: "none" }}>
          <ColumnsButton
            tab="ad_groups"
            visible={visibleCols}
            onChange={setVisibleCols}
            allowedKeys={allowedColumnsForTab("ad_groups", cohortSource)}
            currentWidths={colWidths}
            onApplyWidths={(w) => {
              setColWidths(w);
              saveColumnWidths("ad_groups", w);
            }}
          />
        </div>
      </div>

      {adsFilter?.showChips && agChipsWrapped && (
        <div ref={agChipsRef} className="af-chips-row">
          <FilterChips applied={adsFilter.applied} describe={adsFilter.describe} onOpen={adsFilter.openModal} onRemove={adsFilter.removeType} split={splitChip(adsFilter)} />
        </div>
      )}
      {gate && <div style={{ padding: "0 14px" }}><MappingGateBanner gate={gate} /></div>}

      {listLoading && (
        <div className="muted" style={{ padding: 24, textAlign: "center" }}>
          Loading ad groups…
        </div>
      )}
      {!listLoading && (
        <StatsTable<"ad_group">
          busy={statsQuery.isPlaceholderData}
          deviceSplit={!!adsFilter?.splitOn}
          grain="ad_group"
          rows={filtered}
          emptyContent={
            adsFilter?.isActive ? (
              <FilterEmptyState noun="ad groups" applied={adsFilter.applied} onOpen={adsFilter.openModal} onReset={adsFilter.reset} />
            ) : undefined
          }
          visibleCols={shownCols}
          widths={colWidths}
          onWidthChange={onWidthChange}
          sort={sort}
          onSortChange={setSort}
          selection={{
            ids: selection,
            onToggle: toggle,
            onToggleAll: toggleAll,
          }}
          canEditMoney={canEdit}
          onCommitDefaultBid={(ad_group_id, default_bid) =>
            void runWithAsaConfirmToast({
              mutation: updateAdGroup.mutateAsync({ ad_group_id, default_bid }),
              resourceType: "ad_group",
              fieldLabel: "Default Max CPT bid",
              flash: flashToast,
            })
          }
          onCommitCpaGoal={(ad_group_id, cpa_goal) =>
            void runWithAsaConfirmToast({
              mutation: updateAdGroup.mutateAsync({ ad_group_id, cpa_goal }),
              resourceType: "ad_group",
              fieldLabel: "CPA goal",
              flash: flashToast,
            })
          }
          serverTotals={statsQuery.data?.totals}
          unfilteredTotals={statsQuery.data?.totals}
          deviceTotals={statsQuery.data?.device_totals}
          totalsLabel="Total"
          totalsCount={rows.length}
          totalsCountWord="ad group"
          onRowClick={(row) =>
            onDrillIn(row.ad_group_id, row.ad_group_name ?? `Ad group #${row.ad_group_id}`)
          }
        />
      )}

      <ApplyCppModal
        open={applyCppOpen}
        onClose={() => setApplyCppOpen(false)}
        selectedCount={selectedIds.length}
        entityKind="ad_group"
        cppOptions={cppOptions}
        onRefresh={cppQuery.refresh}
        isRefreshing={cppQuery.isRefreshing}
        onApply={async (cppId) => {
          const creative = cppQuery.data?.rows.find(
            (c) => (c.product_page_id ?? "default") === cppId
          );
          if (!creative) {
            return {
              ok: false as const,
              error: "Pick a Custom Product Page first.",
            };
          }
          try {
            // AdGroups tab applies CPP only to the selected ad_groups,
            // not every ad_group in the campaign — that's the
            // Campaigns-level scope.
            const res = await bulkApplyCpp.mutateAsync({
              ad_group_ids: selectedIds,
              creative_id: creative.creative_id,
              creative_name: creative.name,
              // Carry productPageId + asa adamId so the backend
              // can mint a Creative wrapper when creative_id is 0
              // (freshly-created CPP without Apple's auto-Creative).
              product_page_id: creative.product_page_id ?? null,
              asa_app_id: asaAppId,
            });
            if (res.failed.length === 0) {
              flashToast("CPP applied");
              return { ok: true as const };
            }
            flashToast(
              `Applied to ${res.applied} ad group${res.applied === 1 ? "" : "s"}; ${res.failed.length} failed`,
              "error"
            );
            return {
              ok: false as const,
              error: res.failed
                .slice(0, 3)
                .map((f) => `Ad group ${f.ad_group_id}: ${f.error}`)
                .join("\n"),
            };
          } catch (err) {
            const msg =
              (err as { response?: { data?: { error?: { message?: string } } } })
                ?.response?.data?.error?.message ?? "Couldn't apply CPP";
            flashToast(msg, "error");
            return { ok: false as const, error: msg };
          }
        }}
      />
      <RulesManagerModal
        open={rulesManagerOpen}
        onClose={() => setRulesManagerOpen(false)}
        entityType="ad_group"
        entityIds={selectedIds}
        appId={appId}
        onCreateNew={() => {
          setRulesManagerOpen(false);
          navigate("/rules?create=1");
        }}
      />
      {toast && (
        <StatusToast
          message={toast.msg}
          variant={toast.variant}
          action={toast.action}
        />
      )}
      {agStatusConfirm && (
        <ConfirmDialog
          open
          title={
            agStatusConfirm.target === "PAUSED"
              ? `Pause ${agStatusConfirm.ids.length} ad groups?`
              : `Enable ${agStatusConfirm.ids.length} ad groups?`
          }
          body={
            agStatusConfirm.target === "PAUSED"
              ? "This will stop delivery on all selected ad groups. Their keywords and bids stay configured; you can re-enable later."
              : `This will resume delivery on ${agStatusConfirm.ids.length} selected ad groups.`
          }
          confirmLabel={agStatusConfirm.target === "PAUSED" ? "Pause all" : "Enable all"}
          destructive={agStatusConfirm.target === "PAUSED"}
          onCancel={() => setAgStatusConfirm(null)}
          onConfirm={() => {
            const { target, ids } = agStatusConfirm;
            setAgStatusConfirm(null);
            void runAgBulkStatus(target, ids);
          }}
        />
      )}
    </div>
  );
}

// ===========================================================================
// Keywords tab — flat across the whole campaign, optionally narrowed
// by the Ad Groups tab's checkbox selection.
// ===========================================================================

/** What KeywordsTab exposes upward so the page-level filter strip can
 *  render a Copy button alongside the date picker without owning the
 *  selection state itself. */
export type KeywordsTabExposed = {
  selectedCount: number;
  getSelectedTexts: () => string[];
  /** Единственный выделенный ключ — для встроенного графика. `null` при
   *  нуле и при двух: график по ТЗ строится только по одной сущности. */
  single: { id: number; name: string } | null;
};

function KeywordsTab({
  campaignId,
  cohortSource,
  cohortReady,
  statsFilters,
  filterAdGroupIds,
  drilledAdGroup,
  drilledAdGroupName,
  onExitDrill,
  onEditBids,
  onStatusChanged,
  canEdit,
  appId,
  onExposedChange,
  onToggleChart,
  chartOpen,
}: {
  campaignId: number;
  cohortSource?: CohortSource;
  cohortReady: boolean;
  statsFilters: ReturnType<typeof adsFiltersToStatsFilters>;
  filterAdGroupIds: number[];
  /** When set, we're in drill-down mode (showing one ad-group's
   *  keywords). When null, we're in "All Keywords" mode (whole
   *  campaign). The breadcrumb pill renders only in drill mode. */
  drilledAdGroup: number | null;
  drilledAdGroupName: string | null;
  onExitDrill: () => void;
  /** Open the shared bulk-edit modal pinned to the keyword level,
   *  scoped to the passed selected keyword ids. */
  onEditBids: (ids: number[]) => void;
  /** Notify the parent that a child status flip happened, so it can
   *  pulse-refetch the campaign header (ASA serving status lags). */
  onStatusChanged?: () => void;
  canEdit: boolean;
  /** Приложение кампании — им фильтруется список правил. */
  appId: number | null;
  onExposedChange?: (exposed: KeywordsTabExposed) => void;
  onToggleChart: () => void;
  chartOpen: boolean;
}) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "paused">("all");
  const [visibleCols, _setVisibleColsState] = useState<ColumnKey[]>(() =>
    loadVisibleColumns("keywords")
  );
  // Same wrapped setter pattern as CampaignsPage / AdGroupsTab.
  const setVisibleCols = (
    next: ColumnKey[] | ((prev: ColumnKey[]) => ColumnKey[]),
  ) => {
    _setVisibleColsState((prev) => {
      const resolved = typeof next === "function" ? next(prev) : next;
      saveVisibleColumns("keywords", resolved);
      return resolved;
    });
  };
  const [colWidths, setColWidths] = useState<Record<string, number>>(() =>
    loadColumnWidths("keywords"),
  );
  const onWidthChange = (key: string, px: number) => {
    setColWidths((prev) => {
      const next = { ...prev, [key]: px };
      saveColumnWidths("keywords", next);
      return next;
    });
  };
  // Columns actually rendered, filtered by the app's cohort source.
  const shownCols = useMemo(() => {
    const allowed = new Set(allowedColumnsForTab("keywords", cohortSource));
    return visibleCols.filter((k) => allowed.has(k));
  }, [visibleCols, cohortSource]);
  // Auto-apply the user's default keywords preset on first mount.
  // Matches the pattern in CampaignsPage + AdGroupsTab — and the
  // cohortReady gate (same rationale, see AdGroupsTab comment).
  const presetsQuery = useColumnPresets("keywords");
  const appliedDefaultRef = useRef(false);
  useEffect(() => {
    if (appliedDefaultRef.current) return;
    if (!presetsQuery.data) return;
    if (!cohortReady) return;
    appliedDefaultRef.current = true;
    const dft = presetsQuery.data.find((p) => p.is_default);
    if (!dft) return;
    setVisibleCols(
      resolvePresetColumns(
        "keywords",
        dft.columns,
        allowedColumnsForTab("keywords", cohortSource),
      ),
    );
    const w = dft.widths ?? {};
    setColWidths(w);
    saveColumnWidths("keywords", w);
  }, [presetsQuery.data, cohortReady]); // eslint-disable-line react-hooks/exhaustive-deps
  const [sort, setSort] = useState<{ by: string | null; dir: "asc" | "desc" }>(
    { by: null, dir: "desc" }
  );

  // When ad groups are selected, pass them as ad_group_ids filter. When
  // not, fall back to campaign_ids filter so we fetch the flat list.
  const effectiveFilters = useMemo(
    () =>
      filterAdGroupIds.length > 0
        ? { ...statsFilters, ad_group_ids: filterAdGroupIds }
        : { ...statsFilters, campaign_ids: [campaignId] },
    [statsFilters, campaignId, filterAdGroupIds]
  );

  // useKeywordsTable expects a single ad_group_id — we need a broader
  // fetch, so call the endpoint directly via the filters.
  const kwAdsFilter = useAdsFilterOptional();
  const kwToolbarRef = useRef<HTMLDivElement>(null);
  const kwChipsRef = useRef<HTMLDivElement>(null);
  const kwChipsWrapped = useChipsPlacement(kwToolbarRef, kwChipsRef, !!kwAdsFilter?.showChips);
  // Same as the ad groups tab: search reaches the server only with «Split
  // by devices» on, so the footer's device totals cover the searched set.
  const kwSearchForServer = useDebouncedValue(search, 350).trim();
  const keywordsQuery = useKeywordsTable(
    effectiveFilters,
    // Pass the first selected ad group as a trigger — when none selected,
    // we still want to fetch, so pass campaignId as marker (the hook only
    // disables when null/undefined).
    filterAdGroupIds[0] ?? campaignId,
    { limit: 500 },
    sort,
    kwAdsFilter?.splitOn ? kwSearchForServer || undefined : undefined,
    statusFilter,
    kwAdsFilter?.apiParam,
    kwAdsFilter?.splitOn,
  );

  const serverRows = useMemo<KeywordStatsRow[]>(() => {
    const base = keywordsQuery.data?.rows ?? [];
    const q = search.trim().toLowerCase();
    return base.filter((r) => {
      if (q) {
        const textMatch = (r.keyword_text ?? "").toLowerCase().includes(q);
        const idMatch = String(r.keyword_id ?? "").includes(q);
        if (!textMatch && !idMatch) return false;
      }
      // ASA enum asymmetry: keyword status is ACTIVE/PAUSED (not
      // ENABLED/PAUSED like campaigns / ad_groups). Until 2026-05-10
      // this row compared to "ENABLED" and dropped every active
      // keyword when the filter was set, surfacing the user-visible
      // "No keywords" state on the All Keywords tab.
      return true;
    });
  }, [keywordsQuery.data, search]);

  // See SearchTermsTab: pending is derived from the server rows, not the
  // patched ones (§12).
  const pendingKeywords = useMemo(
    () =>
      serverRows
        .filter((r) => r.translation_status === "pending")
        .map((r) => r.keyword_text ?? ""),
    [serverRows],
  );
  const translationPatches = useTranslationPolling(pendingKeywords);
  const rows = useMemo(
    () =>
      applyTranslationPatches(
        serverRows,
        translationPatches,
        (r) => r.keyword_text,
      ),
    [serverRows, translationPatches],
  );

  const updateKeyword = useUpdateKeyword();
  const [selectedKw, setSelectedKw] = useState<Set<number>>(new Set());
  const selectedKwIds = useMemo(() => Array.from(selectedKw), [selectedKw]);
  const toggleKw = (id: number) => {
    const next = new Set(selectedKw);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedKw(next);
  };
  const toggleAllKw = (ids: number[], selectAll: boolean) => {
    const next = new Set(selectedKw);
    for (const id of ids) {
      if (selectAll) next.add(id);
      else next.delete(id);
    }
    setSelectedKw(next);
  };
  // Confirmation gate for bulk Pause/Enable on keywords. Same defense
  // as on campaigns + ad_groups — silent-mass-mutation incident
  // 2026-05-04 made it the standard for any ≥2 fanout. Toast surfaces
  // an Undo to flip back if it was a misclick.
  const [kwStatusConfirm, setKwStatusConfirm] = useState<
    { target: "ENABLED" | "PAUSED"; ids: number[] } | null
  >(null);
  const [kwRulesManagerOpen, setKwRulesManagerOpen] = useState(false);
  const kwNavigate = useOrgNavigate();
  const [kwToast, setKwToast] = useState<
    {
      msg: string;
      variant: "success" | "error";
      action?: { label: string; onClick: () => void };
    } | null
  >(null);
  const flashKwToast = (
    msg: string,
    variant: "success" | "error" = "success",
    action?: { label: string; onClick: () => void },
  ) => {
    setKwToast({ msg, variant, action });
    window.setTimeout(() => setKwToast(null), action ? 8000 : 5000);
  };

  const fanoutKwStatus = async (
    target: "ENABLED" | "PAUSED",
    ids: number[],
  ): Promise<{
    accepted: { keyword_id: number; pending_write_id: number }[];
    rejected: { keyword_id: number; error: string }[];
  }> => {
    const settled = await Promise.allSettled(
      ids.map(async (keyword_id) => {
        const handle = await updateKeyword.mutateAsync({
          keyword_id,
          status: target,
        });
        return { keyword_id, pending_write_id: handle.pending_write_id };
      }),
    );
    const accepted: { keyword_id: number; pending_write_id: number }[] = [];
    const rejected: { keyword_id: number; error: string }[] = [];
    settled.forEach((r, i) => {
      if (r.status === "fulfilled") accepted.push(r.value);
      else rejected.push({
        keyword_id: ids[i]!,
        error: r.reason instanceof Error ? r.reason.message : String(r.reason),
      });
    });
    return { accepted, rejected };
  };

  const runKwBulkStatus = async (
    target: "ENABLED" | "PAUSED",
    ids: number[],
  ) => {
    if (ids.length === 0) return;
    // Clear the selection on dispatch so the NEXT selection starts
    // fresh instead of accumulating on top of the just-actioned set
    // (`ids` is captured, so the in-flight fanout still completes).
    setSelectedKw(new Set());
    // Enabling/pausing keywords can flip the parent campaign's serving
    // status — kick the parent's header pulse so it converges quickly.
    onStatusChanged?.();
    reportBulkAction({
      resource: "keyword",
      action: target === "PAUSED" ? "pause" : "enable",
      count: ids.length,
      details: { ids: ids.slice(0, 100) },
    });

    const verbPast = target === "PAUSED" ? "paused" : "enabled";
    const reverse: "ENABLED" | "PAUSED" =
      target === "PAUSED" ? "ENABLED" : "PAUSED";

    const { accepted, rejected } = await fanoutKwStatus(target, ids);
    if (rejected.length === ids.length) {
      flashKwToast(
        `Failed to ${target === "PAUSED" ? "pause" : "enable"} ${ids.length} keyword${ids.length === 1 ? "" : "s"}: ${rejected[0]!.error}`,
        "error",
      );
      return;
    }

    const tracked = accepted
      .filter((a) => a.pending_write_id > 0)
      .map((a) => ({ id: a.pending_write_id, resourceType: "keyword" as const }));
    const results = await awaitPendingWrites(tracked);

    let confirmed = 0;
    let failed = rejected.length;
    let timedOut = 0;
    let lastError: string | null = null;
    for (const r of results.values()) {
      if (r.status === "synced") confirmed++;
      else if (r.status === "error") {
        failed++;
        lastError = r.errorMessage ?? lastError;
      }
      else timedOut++;
    }

    if (confirmed === ids.length) {
      flashKwToast(
        `${confirmed} keyword${confirmed === 1 ? "" : "s"} ${verbPast} on ASA`,
        "success",
        {
          label: "Undo",
          onClick: () => {
            void runKwBulkStatus(reverse, ids);
            setKwToast(null);
          },
        },
      );
    } else if (confirmed > 0) {
      const detail =
        failed > 0
          ? `${failed} failed${lastError ? ` (${lastError.slice(0, 80)})` : ""}`
          : `${timedOut} still syncing`;
      flashKwToast(`${confirmed} ${verbPast}, ${detail}`, "error");
    } else if (timedOut === ids.length) {
      flashKwToast(`ASA hasn't confirmed yet — try again in a moment.`, "error");
    } else {
      flashKwToast(
        `Failed to ${target === "PAUSED" ? "pause" : "enable"} ${ids.length} keyword${ids.length === 1 ? "" : "s"}${lastError ? `: ${lastError.slice(0, 80)}` : ""}`,
        "error",
      );
    }
  };

  const bulkSetKwStatus = (target: "ENABLED" | "PAUSED") => {
    if (selectedKwIds.length === 0) return;
    if (selectedKwIds.length >= 2) {
      setKwStatusConfirm({ target, ids: selectedKwIds });
      return;
    }
    void runKwBulkStatus(target, selectedKwIds);
  };

  // Bridge selection + a copy-payload getter up to the parent page so
  // the page-level filter strip can render a Copy button next to the
  // date picker. The closure captures the live `rows` + `selectedKw`
  // each time the effect runs.
  useEffect(() => {
    const one =
      selectedKw.size === 1
        ? (rows.find((r) => selectedKw.has(r.keyword_id)) ?? null)
        : null;
    onExposedChange?.({
      selectedCount: selectedKw.size,
      getSelectedTexts: () =>
        rows
          .filter((r) => selectedKw.has(r.keyword_id))
          .map((r) => r.keyword_text ?? "")
          .filter(Boolean),
      single: one
        ? { id: one.keyword_id, name: one.keyword_text ?? `#${one.keyword_id}` }
        : null,
    });
  }, [selectedKw, rows, onExposedChange]);

  return (
    <div className="table-wrap" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
      <div ref={kwToolbarRef} className="table-toolbar" style={{ flexWrap: "nowrap", gap: 10 }}>
        <div className="input-group" style={{ width: 280, flex: "none" }}>
          {I.search}
          <input
            className="input"
            placeholder="Search keywords"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <SegRange<"all" | "active" | "paused">
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: "all", label: "All" },
            { value: "active", label: "Active" },
            { value: "paused", label: "Paused" },
          ]}
        />
        {drilledAdGroup !== null && (
          // Breadcrumb pill — shows we're inside a single ad-group's
          // keywords, with a button to step back to the campaign-wide
          // "All Keywords" view.
          <button
            type="button"
            className="btn"
            onClick={onExitDrill}
            title="Show all keywords in this campaign"
            style={{ fontSize: 13 }}
          >
            ← All Keywords (currently: {drilledAdGroupName || `ad group #${drilledAdGroup}`})
          </button>
        )}
        <BulkActions
          selectedIds={selectedKwIds}
          canEdit={canEdit}
          onEnable={() => void bulkSetKwStatus("ENABLED")}
          onPause={() => void bulkSetKwStatus("PAUSED")}
          busy={updateKeyword.isPending}
          entityType="keyword"
          onToggleChart={onToggleChart}
          chartOpen={chartOpen}
          showLabel={false}
          showActions
          showRules
          onRulesManager={() => setKwRulesManagerOpen(true)}
          onActionPick={(v) => {
            if (v === "edit_bids") {
              if (selectedKwIds.length) onEditBids(selectedKwIds);
            }
          }}
        />
        {kwAdsFilter?.showChips && !kwChipsWrapped && (
          <div ref={kwChipsRef} style={{ flex: "none" }}>
            <FilterChips applied={kwAdsFilter.applied} describe={kwAdsFilter.describe} onOpen={kwAdsFilter.openModal} onRemove={kwAdsFilter.removeType} split={splitChip(kwAdsFilter)} />
          </div>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flex: "none" }}>
          <ColumnsButton
            tab="keywords"
            visible={visibleCols}
            onChange={setVisibleCols}
            allowedKeys={allowedColumnsForTab("keywords", cohortSource)}
            currentWidths={colWidths}
            onApplyWidths={(w) => {
              setColWidths(w);
              saveColumnWidths("keywords", w);
            }}
          />
        </div>
      </div>

      {kwAdsFilter?.showChips && kwChipsWrapped && (
        <div ref={kwChipsRef} className="af-chips-row">
          <FilterChips applied={kwAdsFilter.applied} describe={kwAdsFilter.describe} onOpen={kwAdsFilter.openModal} onRemove={kwAdsFilter.removeType} split={splitChip(kwAdsFilter)} />
        </div>
      )}
      {keywordsQuery.isLoading && (
        <div className="muted" style={{ padding: 24, textAlign: "center" }}>
          Loading keywords…
        </div>
      )}
      {!keywordsQuery.isLoading && (
        <StatsTable<"keyword">
          busy={keywordsQuery.isPlaceholderData}
          deviceSplit={!!kwAdsFilter?.splitOn}
          grain="keyword"
          rows={rows}
          emptyContent={
            kwAdsFilter?.isActive ? (
              <FilterEmptyState noun="keywords" applied={kwAdsFilter.applied} onOpen={kwAdsFilter.openModal} onReset={kwAdsFilter.reset} />
            ) : undefined
          }
          visibleCols={shownCols}
          widths={colWidths}
          onWidthChange={onWidthChange}
          sort={sort}
          onSortChange={setSort}
          canEditMoney={canEdit}
          onCommitBid={(keyword_id, bid) =>
            void runWithAsaConfirmToast({
              mutation: updateKeyword.mutateAsync({ keyword_id, bid }),
              resourceType: "keyword",
              fieldLabel: "Keyword bid",
              flash: flashKwToast,
            })
          }
          selection={{
            ids: selectedKw,
            onToggle: toggleKw,
            onToggleAll: toggleAllKw,
          }}
          serverTotals={keywordsQuery.data?.totals}
          unfilteredTotals={keywordsQuery.data?.totals}
          deviceTotals={keywordsQuery.data?.device_totals}
          totalsLabel="Total"
          totalsCount={rows.length}
          totalsCountWord="keyword"
        />
      )}
      <RulesManagerModal
        open={kwRulesManagerOpen}
        onClose={() => setKwRulesManagerOpen(false)}
        entityType="keyword"
        entityIds={selectedKwIds}
        appId={appId}
        onCreateNew={() => {
          setKwRulesManagerOpen(false);
          kwNavigate("/rules?create=1");
        }}
      />
      {kwToast && (
        <StatusToast
          message={kwToast.msg}
          variant={kwToast.variant}
          action={kwToast.action}
        />
      )}
      {kwStatusConfirm && (
        <ConfirmDialog
          open
          title={
            kwStatusConfirm.target === "PAUSED"
              ? `Pause ${kwStatusConfirm.ids.length} keywords?`
              : `Enable ${kwStatusConfirm.ids.length} keywords?`
          }
          body={
            kwStatusConfirm.target === "PAUSED"
              ? "This will stop delivery on all selected keywords. Their bids and match types stay configured; you can re-enable later."
              : `This will resume delivery on ${kwStatusConfirm.ids.length} selected keywords.`
          }
          confirmLabel={kwStatusConfirm.target === "PAUSED" ? "Pause all" : "Enable all"}
          destructive={kwStatusConfirm.target === "PAUSED"}
          onCancel={() => setKwStatusConfirm(null)}
          onConfirm={() => {
            const { target, ids } = kwStatusConfirm;
            setKwStatusConfirm(null);
            void runKwBulkStatus(target, ids);
          }}
        />
      )}
    </div>
  );
}

// ===========================================================================
// Negative Keywords tab
// ===========================================================================

function NegativeKeywordsTab({
  campaignId,
  canEdit,
}: {
  campaignId: number;
  canEdit: boolean;
}) {
  const [scope, setScope] = useState<"all" | "campaign" | "ad_group">("all");
  const [search, setSearch] = useState("");
  const query = useNegativeKeywords(campaignId, { scope });
  const cols = useGridColumns("negative_keywords");
  const showTranslation = cols.visible(TRANSLATION_COLUMN);
  const qc = useQueryClient();
  const deleteNegatives = useDeleteNegativeKeywords();

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<
    { msg: string; variant: "success" | "error" } | null
  >(null);
  const flash = (msg: string, variant: "success" | "error" = "success") => {
    setToast({ msg, variant });
    window.setTimeout(() => setToast(null), 5000);
  };

  const serverRows = useMemo(() => {
    const base = query.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter((r) => (r.keyword_text ?? "").toLowerCase().includes(q));
  }, [query.data, search]);

  // See SearchTermsTab: pending is derived from the server rows, not the
  // patched ones (§12).
  const pendingTerms = useMemo(
    () =>
      serverRows
        .filter((r) => r.translation_status === "pending")
        .map((r) => r.keyword_text ?? ""),
    [serverRows],
  );
  const patches = useTranslationPolling(pendingTerms);
  const rows = useMemo(
    () => applyTranslationPatches(serverRows, patches, (r) => r.keyword_text),
    [serverRows, patches],
  );

  // Prune selection to what's still visible after a scope/search change so
  // the count never counts rows the user can no longer see.
  // Список хранится УПОРЯДОЧЕННЫМ, потому что диапазон Shift-выделения
  // считается по строкам в том порядке, в каком они на экране (после
  // поиска и смены scope); множество из него — только для проверок
  // принадлежности.
  const visibleIdList = useMemo(
    () => rows.map((r) => r.negative_keyword_id),
    [rows],
  );
  const visibleIds = useMemo(() => new Set(visibleIdList), [visibleIdList]);
  const selectedVisible = useMemo(
    () => [...selected].filter((id) => visibleIds.has(id)),
    [selected, visibleIds],
  );
  const allVisibleSelected =
    rows.length > 0 && selectedVisible.length === rows.length;

  const toggleOne = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) rows.forEach((r) => next.delete(r.negative_keyword_id));
      else rows.forEach((r) => next.add(r.negative_keyword_id));
      return next;
    });

  // Выделение диапазона с зажатым Shift (как в StatsTable, §55.2). Якорь —
  // последняя строка, которую переключили обычным кликом.
  const anchorRef = useRef<number | null>(null);
  const handleRowToggle = (id: number, shiftKey: boolean) => {
    const anchor = anchorRef.current;
    if (shiftKey && anchor !== null && anchor !== id) {
      const a = visibleIdList.indexOf(anchor);
      const b = visibleIdList.indexOf(id);
      if (a !== -1 && b !== -1) {
        const [lo, hi] = a <= b ? [a, b] : [b, a];
        const range = visibleIdList.slice(lo, hi + 1);
        // Весь диапазон добавляется ОДНИМ обновлением состояния, а не циклом
        // по toggleOne. Причин две. Первая: toggleOne переключает, а не
        // добавляет, — уже выбранные строки внутри диапазона молча снялись
        // бы, хотя Shift обязан только ДОПОЛНЯТЬ выделение. Вторая: любой
        // переключатель, читающий текущее выделение из своего замыкания,
        // в цикле «запомнит» лишь последний идентификатор.
        setSelected((prev) => {
          const next = new Set(prev);
          range.forEach((rid) => next.add(rid));
          return next;
        });
        anchorRef.current = id;
        return;
      }
      // Якорь ушёл из видимых (сменили поиск или scope) — считать диапазон
      // не от чего, обрабатываем как обычный клик.
    }
    toggleOne(id);
    anchorRef.current = id;
  };

  const runDelete = async () => {
    const ids = selectedVisible;
    if (ids.length === 0) return;
    setConfirmOpen(false);
    setDeleting(true);
    // Clear selection up front so a repeat click can't double-fire; the ids
    // are captured in `ids`.
    setSelected(new Set());
    try {
      const res = await deleteNegatives.mutateAsync({
        negative_keyword_ids: ids,
      });
      const handles = (res.pending_write_ids ?? []).map((id) => ({
        id,
        resourceType: "negative_keyword" as const,
      }));
      const results = await awaitPendingWrites(handles);
      const vals = [...results.values()];
      const ok = vals.filter((r) => r.status === "synced").length;
      const errored = vals.find((r) => r.status === "error");
      if (ok === handles.length && handles.length > 0) {
        flash(
          `Deleted ${ids.length} negative keyword${ids.length === 1 ? "" : "s"}`,
        );
      } else if (errored && errored.status === "error") {
        flash(
          `Delete failed: ${errored.errorMessage ?? "ASA rejected the request"}`,
          "error",
        );
      } else {
        flash("ASA hasn't confirmed the delete yet — refresh in a moment.", "error");
      }
    } catch (e) {
      flash(`Delete failed: ${(e as Error).message}`, "error");
    } finally {
      setDeleting(false);
      // Re-read the list — the worker rewrote the mirror from a fresh ASA
      // re-read, so this pulls the authoritative post-delete set.
      void qc.invalidateQueries({ queryKey: ["negative-keywords"] });
    }
  };

  return (
    <div className="table-wrap" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
      <div className="table-toolbar" style={{ flexWrap: "wrap", gap: 10 }}>
        <div className="input-group" style={{ width: 280 }}>
          {I.search}
          <input
            className="input"
            placeholder="Search negative keywords"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="chip-group">
          {(["all", "campaign", "ad_group"] as const).map((s) => (
            <button
              key={s}
              className={scope === s ? "active" : ""}
              onClick={() => setScope(s)}
            >
              {s === "all" ? "All" : s === "campaign" ? "Campaign" : "Ad group"}
            </button>
          ))}
        </div>
        <GridColumnsMenu
          columns={[{ key: TRANSLATION_COLUMN, label: TRANSLATION_LABEL }]}
          visible={cols.visible}
          onToggle={cols.toggle}
        />
        {/* Delete button — sits where the Rules dropdown sits on the
            Search Terms tab, appears once ≥1 negative is selected. */}
        {canEdit && selectedVisible.length > 0 && (
          <button
            className="btn danger"
            disabled={deleting}
            onClick={() => setConfirmOpen(true)}
          >
            {deleting
              ? "Deleting…"
              : `Delete (${selectedVisible.length})`}
          </button>
        )}
      </div>

      {query.isLoading && (
        <div className="muted" style={{ padding: 24, textAlign: "center" }}>
          Loading negative keywords…
        </div>
      )}

      {!query.isLoading && (
        <div className="table-scroll">
          <table className="dt">
            <thead>
              <tr>
                {canEdit && (
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      aria-label="Select all negative keywords"
                      checked={allVisibleSelected}
                      onChange={toggleAll}
                    />
                  </th>
                )}
                <th>Negative keyword</th>
                {showTranslation && (
                  <th title="English reading of the negative keyword. Empty when it is already English or has no translation yet — a reference column, never a gap to chase.">
                    {TRANSLATION_LABEL}
                  </th>
                )}
                <th>Match</th>
                <th>Scope</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={(canEdit ? 1 : 0) + (showTranslation ? 5 : 4)}
                    className="center muted"
                    style={{ padding: 40 }}
                  >
                    {/* 5 data columns, plus the checkbox when editable. */}
                    No negative keywords for this scope.
                  </td>
                </tr>
              )}
              {rows.map((n: NegativeKeywordRow) => (
                <tr key={n.negative_keyword_id}>
                  {canEdit && (
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${n.keyword_text ?? "negative keyword"}`}
                        checked={selected.has(n.negative_keyword_id)}
                        // Состояние Shift доступно только в исходном событии
                        // мыши — синтетическое событие смены его не несёт.
                        onChange={(e) =>
                          handleRowToggle(
                            n.negative_keyword_id,
                            (e.nativeEvent as MouseEvent).shiftKey,
                          )
                        }
                      />
                    </td>
                  )}
                  <td>{n.keyword_text ?? "—"}</td>
                  {showTranslation && (
                    <TranslationCell
                      status={n.translation_status}
                      text={n.translation_en}
                    />
                  )}
                  <td>{n.match_type ?? "—"}</td>
                  <td className="muted">
                    {n.ad_group_id === null ? "Campaign" : `Ad group ${n.ad_group_id}`}
                  </td>
                  <td><StatusDot status={n.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        destructive
        title="Delete negative keywords?"
        body={
          `This will remove ${selectedVisible.length} negative keyword` +
          `${selectedVisible.length === 1 ? "" : "s"} from ASA ` +
          "(from the campaign or ad group they were added to). This can't be undone."
        }
        confirmLabel="Delete"
        onConfirm={() => void runDelete()}
        onCancel={() => setConfirmOpen(false)}
      />
      {toast && <StatusToast message={toast.msg} variant={toast.variant} />}
    </div>
  );
}

// ===========================================================================
// Search Terms tab
// ===========================================================================

// Sentinel text the collector assigns to Apple's "low-volume search
// terms" aggregate (queries too rare to show individually). It carries
// real impressions / spend but isn't an actual query — so it can't be
// added as a negative keyword or driven by a rule. The row is shown but
// its checkbox is disabled.
const LOW_VOLUME_TERM = "low-volume search terms";

// Apple reports share as a fraction (0.3 = 30%): exact below 90 %, one
// low..high band above it — rendered as it comes. A midpoint would invent
// precision Apple deliberately withheld.
function fmtSovPair(lo: number | null, hi: number | null): string {
  if (lo === null || hi === null) return "—";
  const l = Math.round(lo * 100);
  const h = Math.round(hi * 100);
  return l === h ? `${l}%` : `${l}–${h}%`;
}

// Best ALL-slot place over the days; v1 ranks are plain integers.
function fmtSovRank(r: SearchTermRow): string {
  if (r.sov_rank_int !== null && r.sov_rank_int !== undefined) {
    return String(r.sov_rank_int);
  }
  return "—";
}

function SearchTermsTab({
  campaignId,
  canEdit,
  appId,
  statsFilters,
  filterAdGroupIds,
}: {
  campaignId: number;
  /** Per-app edit gate (§51) — the "Add as negative" menu only shows for a
   *  writer of this campaign's app. */
  canEdit: boolean;
  /** Приложение кампании — им фильтруется список правил. */
  appId: number | null;
  statsFilters: ReturnType<typeof adsFiltersToStatsFilters>;
  /** Optional narrowing from the Ad Groups tab's selection — when
   *  one ad group is selected we filter terms to that ad group; when
   *  many are selected we fall back to campaign-wide (the
   *  `/search-terms` endpoint supports exactly one ad_group_id,
   *  which is fine for the one-group-drilldown path the user cares
   *  about). */
  filterAdGroupIds: number[];
}) {
  const [search, setSearch] = useState("");
  // `useSearchTerms` is server-side (unlike AdGroupsTab/KeywordsTab
  // which filter rows in-memory) — debounce so a fast typist doesn't
  // fire one SQL per keystroke.
  const searchDebounced = useDebouncedValue(search, 350);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  // Rows are now one-per-unique-term, so the selection key is the term
  // text (there's no per-row id anymore).
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const adGroupId =
    filterAdGroupIds.length === 1 ? filterAdGroupIds[0] : null;

  const stFilter = useAdsFilterOptional();
  const stSplit = !!stFilter?.splitOn;
  const query = useSearchTerms(campaignId, {
    date_from: statsFilters.date_from,
    date_to: statsFilters.date_to,
    ad_group_id: adGroupId,
    search: searchDebounced || undefined,
    limit,
    offset,
    split: stSplit,
  });

  // Memoised so the `?? []` fallback does not mint a new empty array on every
  // render and re-run everything keyed on it.
  const serverRows = useMemo(() => query.data?.rows ?? [], [query.data]);
  const total = query.data?.total ?? 0;

  // A page normally arrives fully translated — the cache is filled at ingest
  // (§10/C1). `pending` is the exception: a term the hooks missed, or one older
  // than the backfill window. Derived from the SERVER rows, never from the
  // patched ones, or each answer would re-arm the schedule (§12).
  const pendingTerms = useMemo(
    () =>
      serverRows
        .filter((r) => r.translation_status === "pending")
        .map((r) => r.search_term_text),
    [serverRows],
  );
  const patches = useTranslationPolling(pendingTerms);
  const rows = useMemo(
    () => applyTranslationPatches(serverRows, patches, (r) => r.search_term_text),
    [serverRows, patches],
  );

  // Which terms are already added as negatives — drives the red "added as
  // negative" tag in the Source column. A term counts as negated if a
  // campaign-level negative matches its text, OR an ad-group-level negative
  // in one of the term's own ad groups matches. Reads the negatives mirror
  // (asa_negative_keyword_meta), so it lights up once the drainer confirms.
  const cols = useGridColumns("search_terms");
  const showTranslation = cols.visible(TRANSLATION_COLUMN);
  const negativesQuery = useNegativeKeywords(campaignId, { scope: "all" });
  const { campaignNegSet, adGroupNegMap } = useMemo(() => {
    const campSet = new Set<string>();
    const agMap = new Map<number, Set<string>>();
    for (const n of negativesQuery.data ?? []) {
      const t = (n.keyword_text ?? "").trim().toLowerCase();
      if (!t) continue;
      if (n.ad_group_id == null) campSet.add(t);
      else {
        const s = agMap.get(n.ad_group_id) ?? new Set<string>();
        s.add(t);
        agMap.set(n.ad_group_id, s);
      }
    }
    return { campaignNegSet: campSet, adGroupNegMap: agMap };
  }, [negativesQuery.data]);
  const isNegated = (r: (typeof rows)[number]): boolean => {
    const t = (r.search_term_text ?? "").trim().toLowerCase();
    if (!t) return false;
    if (campaignNegSet.has(t)) return true;
    return (r.ad_group_ids ?? []).some((agid) =>
      adGroupNegMap.get(agid)?.has(t),
    );
  };

  // Campaign list for the "To Selected Campaigns" sub-popover. Cached
  // by react-query so reopening the dropdown is free.
  const allCampaignsQuery = useCampaignsTable(statsFilters, { limit: 500 });
  const campaignOptions = (allCampaignsQuery.data?.rows ?? []).map((r) => ({
    id: r.campaign_id,
    name: r.campaign_name ?? `Campaign ${r.campaign_id}`,
  }));

  // "To That Ad Group" only makes sense when the selected term(s) map to
  // exactly ONE ad group total. Each aggregated row carries the list of
  // ad groups the term appeared in; take the union across selection and
  // require size 1 — otherwise the per-ad-group action is ambiguous and
  // the menu disables it (campaign-level actions still work).
  const selectedRows = rows.filter((r) => selected.has(r.search_term_text));
  const adGroupUnion = new Set<number>();
  selectedRows.forEach((r) =>
    (r.ad_group_ids ?? []).forEach((id) => adGroupUnion.add(id)),
  );
  const hasAdGroupContext = selectedRows.length > 0 && adGroupUnion.size === 1;

  // The low-volume aggregate isn't selectable (can't be a negative /
  // rule target), so "select all" + the all-checked state ignore it.
  const selectableRows = rows.filter(
    (r) => r.search_term_text !== LOW_VOLUME_TERM,
  );
  const allOnPageSelected =
    selectableRows.length > 0 &&
    selectableRows.every((r) => selected.has(r.search_term_text));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allOnPageSelected)
      selectableRows.forEach((r) => next.delete(r.search_term_text));
    else selectableRows.forEach((r) => next.add(r.search_term_text));
    setSelected(next);
  };
  const toggleOne = (term: string) => {
    const next = new Set(selected);
    if (next.has(term)) next.delete(term);
    else next.add(term);
    setSelected(next);
  };

  // Rules Manager (search-term grain). Each aggregated row maps to one
  // (campaign, ad_group, text) ref per ad group it appeared in.
  const [stRulesOpen, setStRulesOpen] = useState(false);
  const stNavigate = useOrgNavigate();
  const [stToast, setStToast] = useState<
    { msg: string; variant: "success" | "error" } | null
  >(null);
  const flashStToast = (msg: string, variant: "success" | "error" = "success") => {
    setStToast({ msg, variant });
    window.setTimeout(() => setStToast(null), 5000);
  };
  const searchTermRefs = selectedRows.flatMap((r) =>
    (r.ad_group_ids ?? []).map((agid) => ({
      campaign_id: campaignId,
      ad_group_id: agid,
      text: r.search_term_text,
    })),
  );

  // "Add as negative" — queue the selected terms as negative keywords on the
  // chosen targets (one pending write per target; worker pushes to ASA).
  const createNegatives = useCreateNegativeKeywords();
  const submitNegatives = async (
    targets: { campaign_id: number; ad_group_id?: number | null }[],
    label: string,
  ) => {
    // The low-volume aggregate row isn't a real query — never negate it.
    const texts = selectedRows
      .map((r) => r.search_term_text)
      .filter((t) => t && t !== LOW_VOLUME_TERM);
    if (texts.length === 0 || targets.length === 0) return;
    try {
      await createNegatives.mutateAsync({
        texts,
        match_type: "EXACT",
        targets,
      });
      flashStToast(
        `Queued ${texts.length} negative${texts.length === 1 ? "" : "s"} ${label}`,
      );
      setSelected(new Set());
    } catch (e) {
      flashStToast(`Failed: ${(e as Error).message}`, "error");
    }
  };

  return (
    <div className="table-wrap" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
      <div className="table-toolbar" style={{ flexWrap: "wrap", gap: 10 }}>
        <div className="input-group" style={{ width: 280 }}>
          {I.search}
          <input
            className="input"
            placeholder="Search terms"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOffset(0);
            }}
          />
        </div>
        {canEdit && (
          <AddAsNegativeMenu
            selectedCount={selected.size}
            hasAdGroupContext={hasAdGroupContext}
            campaignOptions={campaignOptions}
            onAddToAdGroup={() => {
              const agid = [...adGroupUnion][0];
              if (agid == null) return;
              void submitNegatives(
                [{ campaign_id: campaignId, ad_group_id: agid }],
                "to this ad group",
              );
            }}
            onAddToCampaign={() =>
              void submitNegatives(
                [{ campaign_id: campaignId }],
                "to this campaign",
              )
            }
            onAddToSelectedCampaigns={(ids) =>
              void submitNegatives(
                ids.map((cid) => ({ campaign_id: cid })),
                `to ${ids.length} campaign${ids.length === 1 ? "" : "s"}`,
              )
            }
          />
        )}
        {selected.size > 0 && (
          <SimpleDropdown
            label="Rules"
            items={[{ value: "rules_manager", label: "Rules Manager" }]}
            onPick={() => setStRulesOpen(true)}
          />
        )}
        <GridColumnsMenu
          columns={[{ key: TRANSLATION_COLUMN, label: TRANSLATION_LABEL }]}
          visible={cols.visible}
          onToggle={cols.toggle}
        />
        <span className="badge">
          <span className="dot" /> {fmtInt(total)} terms
        </span>
        {filterAdGroupIds.length === 1 && (
          <span className="badge accent">Filtered by 1 ad group</span>
        )}
        {filterAdGroupIds.length > 1 && (
          <span className="badge" title="Per-endpoint limitation: pick a single ad group to narrow.">
            {filterAdGroupIds.length} ad groups selected — showing all campaign terms
          </span>
        )}
        {stSplit && (
          <span className="af-chips">
            <SplitChip onRemove={() => stFilter?.setSplit(false)} />
          </span>
        )}
      </div>

      {query.isLoading && (
        <div className="muted" style={{ padding: 24, textAlign: "center" }}>
          Loading search terms…
        </div>
      )}

      {!query.isLoading && (
        <>
          <div className="table-scroll">
            <table className={`dt${stSplit ? " dt-split" : ""}`}>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleAll}
                      aria-label="Select all on this page"
                    />
                  </th>
                  <th>Search term</th>
                  {showTranslation && (
                    <th title="English reading of the search term. Empty when the term is already English or has no translation yet — a reference column, never a gap to chase.">
                      {TRANSLATION_LABEL}
                    </th>
                  )}
                  <th>Geo</th>
                  <th>Source</th>
                  <th className="num">Impressions</th>
                  <th className="num">Taps</th>
                  <th className="num">Installs</th>
                  <th className="num">Spend</th>
                  <th className="num" title="Apple's share of impressions on the FIRST ad place for this query in this country — the APP's share, not this campaign's and not one keyword's. Exact below 90 %, a band above; widened to min..max across the days in this period. Do not sum it.">
                    SOV slot 1
                  </th>
                  <th className="num" title="Share of impressions on the second place: every place minus the first, per day (§4.1), widened across the days.">
                    SOV slot 2
                  </th>
                  <th className="num" title="Share of impressions on ANY ad place for this query in this country. Do not sum it.">
                    SOV total
                  </th>
                  <th className="num" title="Best position our ad held for this query on any day in the period (any place).">
                    Rank
                  </th>
                  <th className="num" title="Apple's own popularity score for the query, 1–5.">
                    Popularity
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={showTranslation ? 14 : 13} className="center muted" style={{ padding: 40 }}>
                      {/* checkbox + 12 data columns + Translation when shown. */}
                      No search terms in this range.
                    </td>
                  </tr>
                )}
                {rows.map((r: SearchTermRow) => {
                  const isLowVol = r.search_term_text === LOW_VOLUME_TERM;
                  return (
                  // Keyed by term AND country: a term in a multi-country campaign
                  // is one row per geo, so the term alone is not unique.
                  <Fragment key={`${r.search_term_text}|${r.country ?? ""}`}>
                  <tr>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(r.search_term_text)}
                        disabled={isLowVol}
                        title={
                          isLowVol
                            ? "Apple's low-volume aggregate — can't be added as a negative or used in a rule"
                            : undefined
                        }
                        onChange={() => toggleOne(r.search_term_text)}
                      />
                    </td>
                    <td className={isLowVol ? "muted" : ""}>{r.search_term_text}</td>
                    {showTranslation && (
                      <TranslationCell
                        status={r.translation_status}
                        text={r.translation_en}
                      />
                    )}
                    <td className={r.country ? "" : "val-muted"}>{r.country ?? "—"}</td>
                    <td className="muted">
                      {r.search_term_source ?? "—"}
                      {isNegated(r) && (
                        <span
                          style={{
                            display: "block",
                            marginTop: 2,
                            fontSize: 11,
                            fontWeight: 600,
                            color: "var(--c-neg-600)",
                          }}
                        >
                          added as negative
                        </span>
                      )}
                    </td>
                    <td className="num">{fmtInt(r.impressions)}</td>
                    <td className="num">{fmtInt(r.taps)}</td>
                    <td className="num">{fmtInt(r.installs)}</td>
                    <td className="num money">${fmtMoney(parseFloat(r.spend_usd))}</td>
                    <td
                      className={r.sov_days > 0 ? "num" : "num val-muted"}
                      title={
                        r.sov_days > 0
                          ? `Apple's share for this query in ${r.country ?? "this geo"}, ` +
                            `across ${r.sov_days} day(s) with data. This is the app's ` +
                            `share of the query — not this campaign's. Don't sum it.`
                          : "No share of voice for this term/day yet"
                      }
                    >
                      {fmtSovPair(r.sov_low_share, r.sov_high_share)}
                    </td>
                    <td className={r.sov_slot2_low_share != null ? "num" : "num val-muted"}>
                      {fmtSovPair(r.sov_slot2_low_share, r.sov_slot2_high_share)}
                    </td>
                    <td className={r.sov_all_low_share != null ? "num" : "num val-muted"}>
                      {fmtSovPair(r.sov_all_low_share, r.sov_all_high_share)}
                    </td>
                    <td className={r.sov_days > 0 ? "num" : "num val-muted"}>
                      {fmtSovRank(r)}
                    </td>
                    <td className={r.sov_search_popularity != null ? "num" : "num val-muted"}>
                      {r.sov_search_popularity ?? "—"}
                    </td>
                  </tr>
                  {stSplit &&
                    deviceSubRows(r.device_split).map((sub) => (
                      // Apple's numbers for this term on one device. Geo, source
                      // and the app-wide share of voice aren't device facts —
                      // empty, like the other sub-rows.
                      <tr key={sub.device} className="dev-sub">
                        <td />
                        <td><span className="dev-sub-label">{sub.label}</span></td>
                        {showTranslation && <td />}
                        <td />
                        <td />
                        <td className="num">{sub.data ? fmtInt(sub.data.impressions) : "—"}</td>
                        <td className="num">{sub.data ? fmtInt(sub.data.taps) : "—"}</td>
                        <td className="num">{sub.data ? fmtInt(sub.data.installs) : "—"}</td>
                        <td className="num money">
                          {sub.data ? `$${fmtMoney(parseFloat(sub.data.spend_usd))}` : "—"}
                        </td>
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                      </tr>
                    ))}
                  </Fragment>
                  );
                })}
              </tbody>
              {stSplit && rows.length > 0 && query.data?.totals && (
                <SearchTermsSplitFooter
                  count={total}
                  totals={query.data.totals}
                  deviceTotals={query.data.device_totals}
                  showTranslation={showTranslation}
                />
              )}
            </table>
          </div>
          <Pager
            total={total}
            limit={limit}
            offset={offset}
            onLimitChange={setLimit}
            onOffsetChange={setOffset}
          />
        </>
      )}
      <RulesManagerModal
        open={stRulesOpen}
        onClose={() => setStRulesOpen(false)}
        entityType="search_term"
        searchTerms={searchTermRefs}
        appId={appId}
        onCreateNew={() => {
          setStRulesOpen(false);
          stNavigate("/rules?create=1");
        }}
      />
      {stToast && (
        <StatusToast message={stToast.msg} variant={stToast.variant} />
      )}
    </div>
  );
}

// ===========================================================================
// Ads tab — ads across all ad groups of the campaign
// ===========================================================================

function AdsTab({
  campaignId,
  statsFilters,
  filterAdGroupIds,
}: {
  campaignId: number;
  statsFilters: ReturnType<typeof adsFiltersToStatsFilters>;
  filterAdGroupIds: number[];
}) {
  const adsSplit = useAdsFilterOptional();
  // One row per CPP rolled up across ad_groups in this campaign (or
  // scoped to a single ad_group when drilled in from the Ad Groups
  // tab). Backend: GET /api/stats/campaign-cpps. Aggregation logic
  // in app/services/cpp_stats_service.py.
  //
  // Date window from the global filter strip (preset / custom date
  // range). statsFilters.date_from / .date_to come pre-formatted.
  const adGroupIdScope =
    filterAdGroupIds.length === 1 ? filterAdGroupIds[0] : null;
  const query = useCampaignCpps({
    campaignId,
    adGroupId: adGroupIdScope,
    dateFrom: statsFilters.date_from,
    dateTo: statsFilters.date_to,
  });

  // Row selection — keyed by the same `${creative_id}-${ad_id}` tuple
  // the table uses for React keys. Sync the set whenever the underlying
  // rows array changes (e.g. date-range switch) so stale selections of
  // CPPs that no longer appear get pruned.
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!query.data?.rows) return;
    const valid = new Set(
      query.data.rows.map((r) => `${r.creative_id}-${r.ad_id}`),
    );
    setSelectedKeys((prev) => {
      const next = new Set([...prev].filter((k) => valid.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [query.data?.rows]);

  const selectedRows = useMemo(() => {
    if (!query.data?.rows) return [];
    return query.data.rows.filter((r) =>
      selectedKeys.has(`${r.creative_id}-${r.ad_id}`),
    );
  }, [query.data?.rows, selectedKeys]);

  const pauseMut = useBulkPauseCpp();
  const applyMut = useBulkApplyCpp();
  const [toast, setToast] = useState<
    { variant: "success" | "error"; message: string } | null
  >(null);
  // StatusToast doesn't auto-dismiss — caller responsibility per its
  // contract. 4s for success, 6s for error so the user has time to
  // read failure details before they fade.
  useEffect(() => {
    if (!toast) return;
    const ttl = toast.variant === "error" ? 6000 : 4000;
    const t = window.setTimeout(() => setToast(null), ttl);
    return () => window.clearTimeout(t);
  }, [toast]);

  // Pause selected CPPs. Backend takes a list of (campaign, ad_group,
  // creative) targets and pauses the live ENABLED Ad in each scope.
  // Idempotent on already-paused targets (counted as success).
  const onPause = async () => {
    const targets = selectedRows.flatMap((r) =>
      r.ad_group_ids.map((agid) => ({
        campaign_id: campaignId,
        ad_group_id: agid,
        creative_id: r.creative_id,
      })),
    );
    if (targets.length === 0) return;
    try {
      const res = await pauseMut.mutateAsync({ targets });
      setSelectedKeys(new Set());
      if (res.failed.length === 0) {
        setToast({
          variant: "success",
          message: `Paused ${res.paused} target${res.paused === 1 ? "" : "s"}.`,
        });
      } else {
        setToast({
          variant: "error",
          message: `Paused ${res.paused}, ${res.failed.length} failed: ${res.failed[0].error}`,
        });
      }
    } catch (e) {
      setToast({
        variant: "error",
        message: `Pause failed: ${e instanceof Error ? e.message : String(e)}`,
      });
    }
  };

  // Play (= Apply CPP via the existing bulk_apply_cpp endpoint).
  // Strict 1-row constraint per Apple's rule: only one ENABLED CPP-Ad
  // per ad_group at a time. The button is disabled when !==1 selected;
  // this is the belt-and-suspenders check in case the disabled state
  // is bypassed.
  const onPlay = async () => {
    if (selectedRows.length !== 1) {
      setToast({
        variant: "error",
        message: "Play activates one CPP at a time. Select exactly one row.",
      });
      return;
    }
    const r = selectedRows[0];
    if (r.creative_id === 0) {
      setToast({
        variant: "error",
        message: "Default Product Page is ASA's implicit fallback — it activates automatically when every CPP Ad is paused.",
      });
      return;
    }
    try {
      const res = await applyMut.mutateAsync({
        ad_group_ids: r.ad_group_ids,
        creative_id: r.creative_id,
        creative_name: r.cpp_name,
        product_page_id: r.product_page_id,
      });
      setSelectedKeys(new Set());
      if (res.failed.length === 0) {
        setToast({
          variant: "success",
          message: `Activated «${r.cpp_name}» in ${res.applied} ad group${res.applied === 1 ? "" : "s"}.`,
        });
      } else {
        setToast({
          variant: "error",
          message: `Activated ${res.applied}, ${res.failed.length} failed: ${res.failed[0].error}`,
        });
      }
    } catch (e) {
      setToast({
        variant: "error",
        message: `Activation failed: ${e instanceof Error ? e.message : String(e)}`,
      });
    }
  };

  const busy = pauseMut.isPending || applyMut.isPending;

  return (
    <div
      className="table-wrap"
      style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
    >
      <div className="table-toolbar" style={{ alignItems: "center" }}>
        {adsSplit?.splitOn && (
          // The split stays on for the other tabs; here Apple has no device
          // numbers for ads, so the chip is dimmed and does nothing.
          <span className="af-chips">
            <SplitChip onRemove={() => adsSplit.setSplit(false)} mutedReason={ADS_TAB_REASON} />
          </span>
        )}
        <span className="muted" style={{ fontSize: 12.5, flex: 1 }}>
          {adGroupIdScope === null
            ? "Per-CPP rollup across all ad groups in this campaign."
            : "Per-CPP rollup for the selected ad group."}
        </span>
        {selectedKeys.size > 0 && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span className="muted" style={{ fontSize: 12.5 }}>
              {selectedKeys.size} selected
            </span>
            <Btn
              icon={I.pause}
              onClick={() => void onPause()}
              disabled={busy}
            >
              Pause
            </Btn>
            <Btn
              variant="primary"
              icon={I.play}
              onClick={() => void onPlay()}
              disabled={busy || selectedRows.length !== 1}
              title={
                selectedRows.length === 1
                  ? "Activate the selected CPP. Pauses any other CPP currently serving in the same ad groups."
                  : "Play activates one CPP at a time — Apple allows only one ENABLED CPP Ad per ad group."
              }
            >
              Play
            </Btn>
          </div>
        )}
      </div>

      {query.isLoading && (
        <div className="muted" style={{ padding: 24, textAlign: "center" }}>
          Loading…
        </div>
      )}

      {!query.isLoading && (query.data?.rows.length ?? 0) === 0 && (
        <div className="muted" style={{ padding: 40, textAlign: "center" }}>
          No ads yet in this campaign for the selected date range.
        </div>
      )}

      {!query.isLoading && (query.data?.rows.length ?? 0) > 0 && (
        <CppRollupTable
          data={query.data!}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
        />
      )}

      {toast && (
        <StatusToast message={toast.message} variant={toast.variant} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CPP rollup table — used by AdsTab. One row per Custom Product Page,
// metrics summed over the date window. Status / preview / ad-group
// summary all rendered inline. Screenshots have an inline strip of
// the first 3 + a hover-popup with the full set.
// ---------------------------------------------------------------------------

// Decimal columns arrive over the wire as JSON strings (Pydantic
// Decimal serialisation). Convert to number for the fmt helpers,
// which take `number | null | undefined`.
function toNum(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

type CppSortKey =
  | "cpp_name"
  | "ad_status"
  | "ad_groups"
  | "cpm"
  | "ipm"
  | "impressions"
  | "ttr"
  | "taps"
  | "avg_cpt"
  | "spend_usd"
  | "installs"
  | "avg_cpa";

type CppSortState = { key: CppSortKey | null; dir: "asc" | "desc" };

// Pull the comparable scalar out of a row for a given sort column.
// Numerics from the backend arrive as `Decimal` JSON strings; toNum
// coerces. Status ordering: Active before Paused on desc (matches
// Campaigns / Ad Groups convention where "active first" is the
// default desc result).
function cppSortValue(row: CampaignCppRow, key: CppSortKey): number | string {
  switch (key) {
    case "cpp_name":
      return (row.cpp_name || "").toLowerCase();
    case "ad_status":
      // "Active" → 1, "Paused" / other → 0. desc puts Active on top.
      return row.ad_status === "Active" ? 1 : 0;
    case "ad_groups":
      return (row.ad_group_names.join(", ") || "").toLowerCase();
    case "cpm":
      return toNum(row.cpm) ?? 0;
    case "ipm":
      return row.ipm ?? 0;
    case "impressions":
      return row.impressions ?? 0;
    case "ttr":
      return row.ttr ?? 0;
    case "taps":
      return row.taps ?? 0;
    case "avg_cpt":
      return toNum(row.avg_cpt) ?? 0;
    case "spend_usd":
      return toNum(row.spend_usd) ?? 0;
    case "installs":
      return row.installs ?? 0;
    case "avg_cpa":
      return toNum(row.avg_cpa) ?? 0;
  }
}

function CppSortableTh({
  sortKey,
  label,
  numeric,
  state,
  setState,
  minWidth,
}: {
  sortKey: CppSortKey;
  label: string;
  numeric?: boolean;
  state: CppSortState;
  setState: (s: CppSortState) => void;
  minWidth?: number;
}) {
  const active = state.key === sortKey;
  const dir = active ? state.dir : null;
  const onClick = () => {
    if (!active) setState({ key: sortKey, dir: "desc" });
    else if (state.dir === "desc") setState({ key: sortKey, dir: "asc" });
    else setState({ key: null, dir: "desc" });
  };
  return (
    <th
      className={[numeric ? "num" : "", "sortable", active ? "is-sorted" : ""]
        .filter(Boolean)
        .join(" ")}
      onClick={onClick}
      style={{
        cursor: "pointer",
        userSelect: "none",
        ...(minWidth ? { minWidth } : null),
        position: "relative",
      }}
    >
      <span className="th-label">{label}</span>
      {active && (
        <span className="sort-indicator" aria-hidden>
          {dir === "desc" ? "▼" : "▲"}
        </span>
      )}
    </th>
  );
}

function CppRollupTable({
  data,
  selectedKeys,
  setSelectedKeys,
}: {
  data: CampaignCppsResponse;
  selectedKeys: Set<string>;
  setSelectedKeys: (next: Set<string>) => void;
}) {
  const [sort, setSort] = useState<CppSortState>({ key: null, dir: "desc" });

  const sortedRows = useMemo(() => {
    if (!sort.key) return data.rows;
    const key = sort.key;
    const sign = sort.dir === "desc" ? -1 : 1;
    return [...data.rows].sort((a, b) => {
      const av = cppSortValue(a, key);
      const bv = cppSortValue(b, key);
      if (typeof av === "string" || typeof bv === "string") {
        return sign * String(av).localeCompare(String(bv));
      }
      return sign * ((av as number) - (bv as number));
    });
  }, [data.rows, sort]);

  // Default Product Page is a synthesised row (creative_id=0); it has
  // no real Ad to pause and ASA serves it implicitly when every CPP
  // Ad in the ad_group is paused. Exclude it from selection so the
  // bulk action set is unambiguous.
  const selectableRows = sortedRows.filter((r) => r.creative_id !== 0);
  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((r) =>
      selectedKeys.has(`${r.creative_id}-${r.ad_id}`),
    );
  const toggleAll = () => {
    if (allSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(
        new Set(selectableRows.map((r) => `${r.creative_id}-${r.ad_id}`)),
      );
    }
  };
  const toggleOne = (key: string) => {
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedKeys(next);
  };

  return (
    <div className="table-scroll">
      <table className="dt">
        <thead>
          <tr>
            <th style={{ width: 36 }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                disabled={selectableRows.length === 0}
                aria-label="Select all CPPs"
              />
            </th>
            <CppSortableTh sortKey="cpp_name" label="Ad name" state={sort} setState={setSort} minWidth={180} />
            <CppSortableTh sortKey="ad_status" label="Status" state={sort} setState={setSort} />
            <th style={{ minWidth: 140 }}>Preview</th>
            <CppSortableTh sortKey="ad_groups" label="Ad groups" state={sort} setState={setSort} minWidth={180} />
            <CppSortableTh sortKey="cpm" label="CPM" numeric state={sort} setState={setSort} />
            <CppSortableTh sortKey="ipm" label="IPM" numeric state={sort} setState={setSort} />
            <CppSortableTh sortKey="impressions" label="Impressions" numeric state={sort} setState={setSort} />
            <CppSortableTh sortKey="ttr" label="TTR" numeric state={sort} setState={setSort} />
            <CppSortableTh sortKey="taps" label="Taps" numeric state={sort} setState={setSort} />
            <CppSortableTh sortKey="avg_cpt" label="Avg CPT" numeric state={sort} setState={setSort} />
            <CppSortableTh sortKey="spend_usd" label="Spend" numeric state={sort} setState={setSort} />
            <CppSortableTh sortKey="installs" label="Installs" numeric state={sort} setState={setSort} />
            <CppSortableTh sortKey="avg_cpa" label="Avg CPA" numeric state={sort} setState={setSort} />
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((r) => {
            const key = `${r.creative_id}-${r.ad_id}`;
            return (
              <CppRollupRow
                key={key}
                row={r}
                selected={selectedKeys.has(key)}
                onToggle={() => toggleOne(key)}
              />
            );
          })}
        </tbody>
        {data.totals && (
          <tfoot>
            <tr className="totals-row">
              <td colSpan={5}>
                <strong>Total</strong>
              </td>
              <td className="num">{fmtMoneyTrim(toNum(data.totals.cpm))}</td>
              <td className="num"><IpmBadge v={data.totals.ipm} /></td>
              <td className="num">{fmtInt(data.totals.impressions)}</td>
              <td className="num">{fmtPct(data.totals.ttr)}</td>
              <td className="num">{fmtInt(data.totals.taps)}</td>
              <td className="num">{fmtMoneyTrim(toNum(data.totals.avg_cpt))}</td>
              <td className="num">{fmtMoneyTrim(toNum(data.totals.spend_usd))}</td>
              <td className="num">{fmtInt(data.totals.installs)}</td>
              <td className="num">{fmtMoneyTrim(toNum(data.totals.avg_cpa))}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function CppRollupRow({
  row,
  selected,
  onToggle,
}: {
  row: CampaignCppRow;
  selected: boolean;
  onToggle: () => void;
}) {
  const adGroupsText = (() => {
    if (row.is_all_ad_groups) return "all ad groups";
    if (row.ad_group_count_in_scope === 1) {
      return row.ad_group_names[0] || `ad group ${row.ad_group_ids[0]}`;
    }
    return `${row.ad_group_count_in_scope} ad groups`;
  })();
  // Default Product Page (creative_id=0) — synthesised row. No real
  // Ad to act on; checkbox stays disabled so user can't include it
  // in a Pause/Play bulk action. The visual still renders the row so
  // metrics are visible.
  const isDefault = row.creative_id === 0;
  return (
    <tr>
      <td>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          disabled={isDefault}
          aria-label="Select CPP"
          title={
            isDefault
              ? "Default Product Page is ASA's implicit fallback — no bulk action applies."
              : undefined
          }
        />
      </td>
      <td>{row.cpp_name}</td>
      <td>
        <StatusDot status={row.ad_status} />
      </td>
      <td className="cpp-preview-td">
        <ScreenshotsCell row={row} />
      </td>
      <td title={row.ad_group_names.join(", ")}>{adGroupsText}</td>
      <td className="num">{fmtMoneyTrim(toNum(row.cpm))}</td>
      <td className="num"><IpmBadge v={row.ipm} /></td>
      <td className="num">{fmtInt(row.impressions)}</td>
      <td className="num">{fmtPct(row.ttr)}</td>
      <td className="num">{fmtInt(row.taps)}</td>
      <td className="num">{fmtMoneyTrim(toNum(row.avg_cpt))}</td>
      <td className="num">{fmtMoneyTrim(toNum(row.spend_usd))}</td>
      <td className="num">{fmtInt(row.installs)}</td>
      <td className="num">{fmtMoneyTrim(toNum(row.avg_cpa))}</td>
    </tr>
  );
}

function ScreenshotsCell({ row }: { row: CampaignCppRow }) {
  const all = row.screenshots_all.length ? row.screenshots_all : row.screenshots;
  const inline = row.screenshots.slice(0, 3);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  if (inline.length === 0) {
    return <span className="muted">—</span>;
  }

  // .table-scroll has overflow:auto; absolute-positioned popup gets
  // clipped by it. Use position:fixed with coords pinned to the
  // cell's bounding rect so it floats over the scroll container.
  const open = () => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.bottom + 6, left: r.left });
  };
  const close = () => setPos(null);

  return (
    <div
      className="cpp-preview-cell"
      ref={wrapRef}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
      tabIndex={0}
    >
      <div className="cpp-preview-thumbs">
        {inline.map((url, i) => (
          <img
            key={i}
            src={url}
            alt=""
            className="cpp-preview-thumb"
            loading="lazy"
          />
        ))}
      </div>
      {pos && all.length > 0 && (
        <div
          className="cpp-preview-popup"
          style={{ position: "fixed", top: pos.top, left: pos.left }}
        >
          {all.map((url, i) => (
            <img
              key={i}
              src={url}
              alt=""
              className="cpp-preview-popup-thumb"
              loading="lazy"
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================

function toQueryString(f: AdsFilters): string {
  const p = new URLSearchParams();
  if (f.preset !== "7d") p.set("preset", f.preset);
  if (f.mode === "static") p.set("mode", "static");
  if (f.preset === "custom" || f.mode === "static") {
    p.set("from", ymd(f.range.from));
    p.set("to", ymd(f.range.to));
  }
  if (f.appId !== "all") p.set("app", String(f.appId));
  const s = p.toString();
  return s ? `?${s}` : "";
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
